/**
 * Internationalization (i18n) utility
 * Supports English, Chinese (Simplified), Spanish, Japanese, and Portuguese
 */

import React from 'react';

const translations = {
  en: {
    eventLibrary: 'Event library',
    searchEvents: 'Search events…',
    closeEventLibrary: 'Close event library',
    noEventsFound: 'No events found',
    otherEvents: 'Other',
    eventsLabel: 'events',

    // eSIM Ad
    supportUs: 'Support us - ',
    esimTitle: 'Get an eSIM with Estonian number, no monthly fee',
    esimDescription: 'Works in 144 countries • Starting from €4 • From the author of majors.im',
    esimDescription2: 'For €33 you can get 50GB data works in EU, UK, US, China, Malaysia and Thailand',
    esimCta: 'Get eSIM',
    esimFeatures: 'No contracts • No monthly fees • Cheap data plans',
    esimSanctions: 'This does not work in Russia due to sanctions',

    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'For ads/business partnerships, contact',

    // Event Selector
    selectEvent: 'Select Event',
    languageAuto: 'Auto',

    // About Menu
    about: 'About',
    majorRulebook: 'Major Rulebook',
    majorRulebookSubtitleCS: 'The Valve Rulebook',
    majorRulebookSubtitleR6: 'R6 Major Rules',
    discord: 'Discord',
    discordSubtitle: 'Feedback, or just chat',
    kofi: 'Ko-fi Page',
    kofiSubtitle: 'Donate to this site :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'A quick guide to using this site',
    steamProfile: 'Steam Profile',
    steamProfileSubtitle: 'Comment or give profile awards',

    // UI Types
    officialUI: 'Simple View',
    classicUI: 'Classic View',
    bracketUI: 'Bracket View',
    minimalUI: 'Minimal View',

    // Customize
    customize: 'Customize',
    displayMatchesOnly: 'Matches Only',
    hideEliminatedTeams: 'Hide Eliminated Teams',
    showBestOfIndicator: 'Show Best-of',
    showTeamStandings: 'Show Standings',
    teamAbbreviations: 'Team Abbreviations',
    shortTeamNames: 'Short Team Names',
    themeMode: 'Theme',
    systemTheme: 'System',
    lightTheme: 'Light',
    darkTheme: 'Dark',

    // Actions
    advanceToNextStage: 'Advance to Next Stage',
    chooseUIType: 'View',
    statusQualified: 'qualified',
    statusQuarterfinalist: 'quarterfinalist',
    statusSemifinalist: 'semifinalist',
    statusRunnerUp: 'runner-up',
    statusChampion: 'champion',
    statusEliminated: 'eliminated',
    statusAdvanced: 'advanced',
    finalResults: 'Final Results',
    finalResult: 'Final Result',
    roundLabel: 'Round',

    // How to Use
    howToUse: 'How to Use',
    howToUseIntro: 'This simulator is a prediction tool: choose winners, watch the bracket recalculate, and compare how different paths affect advancement.',
    howToUseStep1: 'Click the team you expect to win each match',
    howToUseStep2: 'After all winners in a round are selected, the next round updates with the resulting matchups',
    howToUseStep3: 'When a stage is complete, advance the qualified teams into the next stage',
    howToUsePickTitle: 'Pick winners',
    howToUseSimTitle: 'Let the bracket recalculate',
    howToUseAdvanceTitle: 'Move stages forward',
    howToUseViewsTitle: 'Switch views',
    howToUseViewsBody: 'Use Minimal for fast picks, Bracket for the full path, and Classic when you want dense standings and match detail.',
    howToUseLocksTitle: 'Lock important picks',
    howToUseLocksBody: 'Clicking the currently selected winner again locks that match, so shuffle or later changes keep that result fixed.',
    pickemLockScenarioHelp: 'Click your selected winner again to lock a match result and recalculate Pick’em survival for that scenario.',
    howToUseStandingsTitle: 'Read standings context',
    howToUseStandingsBody: 'Buchholz and seed/order notes explain why teams are paired or ranked a certain way in Swiss-style stages.',
    close: 'Close',
  },
  zh: {
    eventLibrary: '赛事库',
    searchEvents: '搜索赛事…',
    closeEventLibrary: '关闭赛事库',
    noEventsFound: '未找到赛事',
    otherEvents: '其他',
    eventsLabel: '项赛事',

    // eSIM Ad
    supportUs: '支持我们 - ',
    esimTitle: '获取爱沙尼亚号码 eSIM，无月费',
    esimDescription: '覆盖 144 个国家/地区 • 起价 €4 • 来自 majors.im 作者',
    esimDescription2: '€33 可获得 50GB 数据，适用于欧盟、英国、美国、中国、马来西亚和泰国',
    esimCta: '获取 eSIM',
    esimFeatures: '无合约 • 无月费 • 低价数据套餐',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: '广告/商业合作请联系',

    // Event Selector
    selectEvent: '选择赛事',
    languageAuto: '自动',

    // About Menu
    about: '关于',
    majorRulebook: 'Major 规则手册',
    majorRulebookSubtitleCS: 'Valve 规则手册',
    majorRulebookSubtitleR6: 'R6 Major 规则',
    discord: 'Discord',
    discordSubtitle: '反馈，或随便聊聊',
    kofi: 'Ko-fi 页面',
    kofiSubtitle: '为本站捐款 :D',
    tutorial: '教程',
    tutorialSubtitle: '快速了解如何使用本站',
    steamProfile: 'Steam 个人资料',
    steamProfileSubtitle: '留言或赠送个人资料奖励',

    // UI Types
    classicUI: '经典视图',
    bracketUI: '对阵视图',
    minimalUI: '简洁视图',

    // Customize
    customize: '自定义',
    displayMatchesOnly: '仅显示比赛',
    hideEliminatedTeams: '隐藏已淘汰队伍',
    showBestOfIndicator: '显示赛制',
    showTeamStandings: '显示排名',
    teamAbbreviations: '队伍缩写',
    themeMode: '主题',
    systemTheme: '跟随系统',
    lightTheme: '浅色',
    darkTheme: '深色',
    
    // Actions
    advanceToNextStage: '晋级到下一阶段',
    chooseUIType: '视图',
    statusQualified: '出线',
    statusQuarterfinalist: '八强',
    statusSemifinalist: '四强',
    statusRunnerUp: '亚军',
    statusChampion: '冠军',
    statusEliminated: '淘汰',
    statusAdvanced: '晋级',
    finalResults: '最终结果',
    finalResult: '最终结果',
    roundLabel: '第',

    // How to Use
    howToUse: '使用说明',
    howToUseIntro: '这是一个预测工具：选择胜者，观察对阵自动重算，并比较不同赛果路线如何影响晋级。',
    howToUseStep1: '点击你认为会获胜的队伍',
    howToUseStep2: '选完本轮所有胜者后，下一轮会显示对应的新对阵',
    howToUseStep3: '阶段结束后，点击晋级即可把出线队伍带入下一阶段',
    howToUsePickTitle: '选择胜者',
    howToUseSimTitle: '让对阵自动重算',
    howToUseAdvanceTitle: '推进阶段',
    howToUseViewsTitle: '切换视图',
    howToUseViewsBody: '快速预测用简洁视图，查看完整路径用对阵视图，需要紧凑排名和比赛细节时用经典视图。',
    howToUseLocksTitle: '锁定关键预测',
    howToUseLocksBody: '再次点击当前选中的胜者会锁定该比赛，之后随机或其他改动不会覆盖这个结果。',
    pickemLockScenarioHelp: '再次点击已选中的胜者即可锁定该比赛结果，并按这个情景重新计算 Pick’em 存活率。',
    howToUseStandingsTitle: '理解排名依据',
    howToUseStandingsBody: 'Buchholz、种子和排序信息会解释瑞士轮阶段为什么这样配对或排名。',
    close: '关闭',
  },
  es: {
    eventLibrary: 'Biblioteca de eventos',
    searchEvents: 'Buscar eventos…',
    closeEventLibrary: 'Cerrar biblioteca',
    noEventsFound: 'No se encontraron eventos',
    otherEvents: 'Otros',
    eventsLabel: 'eventos',

    // eSIM Ad
    supportUs: 'Apóyanos - ',
    esimTitle: 'Obtén un eSIM con número estonio, sin cuota mensual',
    esimDescription: 'Funciona en 144 países • Desde €4 • Del autor de majors.im',
    esimDescription2: 'Por €33 puedes obtener 50GB de datos que funciona en UE, Reino Unido, EE. UU., China, Malasia y Tailandia',
    esimCta: 'Obtener eSIM',
    esimFeatures: 'Sin contratos • Sin cuotas mensuales • Planes baratos',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Para publicidad/colaboraciones comerciales, contacta a',

    // Event Selector
    selectEvent: 'Seleccionar Evento',
    languageAuto: 'Automático',

    // About Menu
    about: 'Acerca de',
    majorRulebook: 'Reglamento Major',
    majorRulebookSubtitleCS: 'El Reglamento de Valve',
    majorRulebookSubtitleR6: 'Reglas Major R6',
    discord: 'Discord',
    discordSubtitle: 'Comentarios o simplemente charla',
    kofi: 'Página Ko-fi',
    kofiSubtitle: 'Donar a este sitio :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'Una guía rápida para usar este sitio',
    steamProfile: 'Perfil de Steam',
    steamProfileSubtitle: 'Comentar o dar premios de perfil',

    // UI Types
    classicUI: 'Vista clásica',
    bracketUI: 'Vista de cuadro',
    minimalUI: 'Vista mínima',

    // Customize
    customize: 'Personalizar',
    displayMatchesOnly: 'Solo partidos',
    hideEliminatedTeams: 'Ocultar equipos eliminados',
    showBestOfIndicator: 'Mostrar formato',
    showTeamStandings: 'Mostrar clasificación',
    teamAbbreviations: 'Abreviaturas de equipos',
    themeMode: 'Tema',
    systemTheme: 'Sistema',
    lightTheme: 'Claro',
    darkTheme: 'Oscuro',
    
    // Actions
    advanceToNextStage: 'Avanzar a la siguiente fase',
    chooseUIType: 'Vista',
    statusQualified: 'clasificado',
    statusQuarterfinalist: 'cuartofinalista',
    statusSemifinalist: 'semifinalista',
    statusRunnerUp: 'subcampeón',
    statusChampion: 'campeón',
    statusEliminated: 'eliminado',
    statusAdvanced: 'clasificado',
    finalResults: 'Resultados finales',
    finalResult: 'Resultado final',
    roundLabel: 'Ronda',

    // How to Use
    howToUse: 'Cómo Usar',
    howToUseIntro: 'Este simulador sirve para predecir: elige ganadores, deja que el cuadro se recalcule y compara cómo distintos caminos afectan la clasificación.',
    howToUseStep1: 'Haz clic en el equipo que crees que ganará cada partido',
    howToUseStep2: 'Cuando elijas todos los ganadores de una ronda, la siguiente ronda se actualizará con los cruces resultantes',
    howToUseStep3: 'Cuando termine una fase, avanza los equipos clasificados a la siguiente fase',
    howToUsePickTitle: 'Elige ganadores',
    howToUseSimTitle: 'Recalcula el cuadro',
    howToUseAdvanceTitle: 'Avanza de fase',
    howToUseViewsTitle: 'Cambia de vista',
    howToUseViewsBody: 'Usa Mínima para elegir rápido, Cuadro para ver todo el camino y Clásica para rankings compactos y más detalle.',
    howToUseLocksTitle: 'Bloquea picks importantes',
    howToUseLocksBody: 'Haz clic otra vez en el ganador seleccionado para bloquear el partido, de modo que shuffle u otros cambios mantengan ese resultado.',
    pickemLockScenarioHelp: 'Haz clic otra vez en el ganador seleccionado para fijar ese resultado y recalcular la supervivencia del Pick’em en ese escenario.',
    howToUseStandingsTitle: 'Lee el contexto de clasificación',
    howToUseStandingsBody: 'Buchholz y las notas de seed/orden explican por qué los equipos se emparejan o clasifican de cierta forma en fases suizas.',
    close: 'Cerrar',
  },
  ja: {
    eventLibrary: '大会ライブラリ',
    searchEvents: '大会を検索…',
    closeEventLibrary: 'ライブラリを閉じる',
    noEventsFound: '大会が見つかりません',
    otherEvents: 'その他',
    eventsLabel: '大会',

    // eSIM Ad
    supportUs: 'サポート - ',
    esimTitle: 'エストニア番号付きeSIMを取得、月額料金なし',
    esimDescription: '144カ国で利用可能 • €4から • majors.imの作者による',
    esimDescription2: '€33で50GBのデータを取得できます。EU、英国、米国、中国、マレーシア、タイで利用可能',
    esimCta: 'eSIMを取得',
    esimFeatures: '契約不要 • 月額料金なし • 安価なプラン',
    esimSanctions: '',

    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: '広告/ビジネスパートナーシップについては、',

    // Event Selector
    selectEvent: 'イベントを選択',
    languageAuto: '自動',

    // About Menu
    about: 'について',
    majorRulebook: 'Majorルールブック',
    majorRulebookSubtitleCS: 'Valveルールブック',
    majorRulebookSubtitleR6: 'R6 Majorルール',
    discord: 'Discord',
    discordSubtitle: 'フィードバックや雑談',
    kofi: 'Ko-fiページ',
    kofiSubtitle: 'このサイトに寄付 :D',
    tutorial: 'チュートリアル',
    tutorialSubtitle: 'このサイトの使い方を短く説明します',
    steamProfile: 'Steamプロフィール',
    steamProfileSubtitle: 'コメントまたはプロフィールアワードを贈る',

    // UI Types
    classicUI: 'クラシック表示',
    bracketUI: 'ブラケット表示',
    minimalUI: 'ミニマル表示',

    // Customize
    customize: 'カスタマイズ',
    displayMatchesOnly: '試合のみ表示',
    hideEliminatedTeams: '敗退チームを非表示',
    showBestOfIndicator: '試合形式を表示',
    showTeamStandings: '順位を表示',
    teamAbbreviations: 'チーム略称',
    themeMode: 'テーマ',
    systemTheme: 'システム',
    lightTheme: 'ライト',
    darkTheme: 'ダーク',

    // Actions
    advanceToNextStage: '次のステージへ進む',
    chooseUIType: '表示形式',
    statusQualified: '出場権獲得',
    statusQuarterfinalist: '準々決勝進出',
    statusSemifinalist: '準決勝進出',
    statusRunnerUp: '準優勝',
    statusChampion: '優勝',
    statusEliminated: '敗退',
    statusAdvanced: '進出',
    finalResults: '最終結果',
    finalResult: '最終結果',
    roundLabel: 'ラウンド',

    // How to Use
    howToUse: '使い方',
    howToUseIntro: 'このシミュレーターは予想用ツールです。勝者を選び、ブラケットの再計算を見ながら、別ルートが進出条件にどう影響するか比較できます。',
    howToUseStep1: '各試合で勝つと思うチームをクリックします',
    howToUseStep2: 'ラウンド内の勝者をすべて選ぶと、次のラウンドの対戦が更新されます',
    howToUseStep3: 'ステージが完了したら、進出チームを次のステージへ送ります',
    howToUsePickTitle: '勝者を選ぶ',
    howToUseSimTitle: 'ブラケットを再計算',
    howToUseAdvanceTitle: 'ステージを進める',
    howToUseViewsTitle: '表示を切り替える',
    howToUseViewsBody: '素早く選ぶならミニマル、全体の流れを見るならブラケット、順位と詳細を詰めて見るならクラシックを使います。',
    howToUseLocksTitle: '重要な予想を固定',
    howToUseLocksBody: '現在選ばれている勝者をもう一度クリックすると、その試合が固定され、シャッフルや後の変更で上書きされません。',
    pickemLockScenarioHelp: '選択済みの勝者をもう一度クリックすると試合結果を固定し、そのシナリオでのPick’em生存率を再計算できます。',
    howToUseStandingsTitle: '順位の根拠を見る',
    howToUseStandingsBody: 'Buchholz、シード、並び順の情報から、スイス形式での対戦や順位の理由を確認できます。',
    close: '閉じる',
  },
  pt: {
    eventLibrary: 'Biblioteca de eventos',
    searchEvents: 'Pesquisar eventos…',
    closeEventLibrary: 'Fechar biblioteca',
    noEventsFound: 'Nenhum evento encontrado',
    otherEvents: 'Outros',
    eventsLabel: 'eventos',

    // eSIM Ad
    supportUs: 'Apoie-nos - ',
    esimTitle: 'Obtenha um eSIM com número estoniano, sem taxa mensal',
    esimDescription: 'Funciona em 144 países • A partir de €4 • Do autor de majors.im',
    esimDescription2: 'Por €33 você pode obter 50GB de dados que funciona na UE, Reino Unido, EUA, China, Malásia e Tailândia',
    esimCta: 'Obter eSIM',
    esimFeatures: 'Sem contratos • Sem taxas mensais • Planos baratos',
    esimSanctions: '',

    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Para publicidade/parcerias comerciais, entre em contato',

    // Event Selector
    selectEvent: 'Selecionar Evento',
    languageAuto: 'Automático',

    // About Menu
    about: 'Sobre',
    majorRulebook: 'Regulamento Major',
    majorRulebookSubtitleCS: 'O Regulamento da Valve',
    majorRulebookSubtitleR6: 'Regras Major R6',
    discord: 'Discord',
    discordSubtitle: 'Feedback ou conversa casual',
    kofi: 'Página Ko-fi',
    kofiSubtitle: 'Doe para este site :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'Um guia rápido para usar este site',
    steamProfile: 'Perfil Steam',
    steamProfileSubtitle: 'Comentar ou dar prêmios de perfil',

    // UI Types
    classicUI: 'Vista clássica',
    bracketUI: 'Vista de chave',
    minimalUI: 'Vista mínima',

    // Customize
    customize: 'Personalizar',
    displayMatchesOnly: 'Apenas partidas',
    hideEliminatedTeams: 'Ocultar equipes eliminadas',
    showBestOfIndicator: 'Mostrar formato',
    showTeamStandings: 'Mostrar classificação',
    teamAbbreviations: 'Abreviações das equipes',
    themeMode: 'Tema',
    systemTheme: 'Sistema',
    lightTheme: 'Claro',
    darkTheme: 'Escuro',

    // Actions
    advanceToNextStage: 'Avançar para a próxima fase',
    chooseUIType: 'Vista',
    statusQualified: 'classificado',
    statusQuarterfinalist: 'quartas de final',
    statusSemifinalist: 'semifinalista',
    statusRunnerUp: 'vice-campeão',
    statusChampion: 'campeão',
    statusEliminated: 'eliminado',
    statusAdvanced: 'classificado',
    finalResults: 'Resultados finais',
    finalResult: 'Resultado final',
    roundLabel: 'Rodada',

    // How to Use
    howToUse: 'Como Usar',
    howToUseIntro: 'Este simulador é uma ferramenta de previsão: escolha vencedores, veja a chave recalcular e compare como caminhos diferentes afetam a classificação.',
    howToUseStep1: 'Clique na equipe que você espera que vença cada partida',
    howToUseStep2: 'Depois de escolher todos os vencedores de uma rodada, a próxima rodada será atualizada com os confrontos resultantes',
    howToUseStep3: 'Quando uma fase terminar, avance as equipes classificadas para a fase seguinte',
    howToUsePickTitle: 'Escolha vencedores',
    howToUseSimTitle: 'Recalcule a chave',
    howToUseAdvanceTitle: 'Avance fases',
    howToUseViewsTitle: 'Troque de visualização',
    howToUseViewsBody: 'Use Mínima para palpites rápidos, Chave para ver o caminho completo e Clássica para classificação compacta e detalhes.',
    howToUseLocksTitle: 'Trave palpites importantes',
    howToUseLocksBody: 'Clique novamente no vencedor selecionado para travar a partida, mantendo o resultado em embaralhamentos ou mudanças posteriores.',
    pickemLockScenarioHelp: 'Clique novamente no vencedor selecionado para fixar o resultado e recalcular a sobrevivência do Pick’em nesse cenário.',
    howToUseStandingsTitle: 'Leia o contexto da tabela',
    howToUseStandingsBody: 'Buchholz e notas de seed/ordem explicam por que equipes são pareadas ou ranqueadas de certa forma em fases suíças.',
    close: 'Fechar',
  },
  ru: {
    // eSIM Ad
    supportUs: 'Поддержите нас - ',
    esimTitle: 'Получите eSIM с эстонским номером, без ежемесячной платы',
    esimDescription: 'Работает в 144 странах • От €4 • От автора majors.im',
    esimDescription2: 'За €33 вы можете получить 50 ГБ данных, работает в ЕС, Великобритании, США, Китае, Малайзии и Таиланде',
    esimCta: 'Получить eSIM',
    esimFeatures: 'Без контрактов • Без ежемесячной платы • Дешевые тарифы',
    esimSanctions: 'Это не работает в России из-за санкций',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Для рекламы/бизнес-партнерства, свяжитесь',
    
    // Event Selector
    selectEvent: 'Выбрать событие',
    languageAuto: 'Авто',
    
    // About Menu
    about: 'О нас',
    majorRulebook: 'Правила Major',
    majorRulebookSubtitleCS: 'Правила Valve',
    majorRulebookSubtitleR6: 'Правила R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Отзывы или просто чат',
    kofi: 'Страница Ko-fi',
    kofiSubtitle: 'Пожертвовать на сайт :D',
    tutorial: 'Руководство',
    tutorialSubtitle: 'Краткое руководство по использованию сайта',
    steamProfile: 'Профиль Steam',
    steamProfileSubtitle: 'Оставить комментарий или награду профиля',
    
    // UI Types
    classicUI: 'Классический вид',
    bracketUI: 'Вид сетки',
    minimalUI: 'Минимальный вид',
    
    // Customize
    customize: 'Настройки',
    displayMatchesOnly: 'Только матчи',
    hideEliminatedTeams: 'Скрыть выбывшие команды',
    showBestOfIndicator: 'Показать формат',
    showTeamStandings: 'Показать таблицу',
    teamAbbreviations: 'Сокращения команд',
    themeMode: 'Тема',
    systemTheme: 'Системная',
    lightTheme: 'Светлая',
    darkTheme: 'Темная',
    
    // Actions
    advanceToNextStage: 'Перейти к следующему этапу',
    chooseUIType: 'Вид',
    statusQualified: 'квалифицировалась',
    statusQuarterfinalist: 'четвертьфиналист',
    statusSemifinalist: 'полуфиналист',
    statusRunnerUp: 'финалист',
    statusChampion: 'чемпион',
    statusEliminated: 'выбыла',
    statusAdvanced: 'прошла',
    finalResults: 'Итоговые результаты',
    finalResult: 'Итоговый результат',
    roundLabel: 'Раунд',
    
    // How to Use
    howToUse: 'Как использовать',
    howToUseIntro: 'Этот симулятор помогает делать прогнозы: выбирайте победителей, смотрите пересчет сетки и сравнивайте, как разные пути влияют на проход дальше.',
    howToUseStep1: 'Нажмите на команду, которая, по вашему мнению, выиграет матч',
    howToUseStep2: 'После выбора всех победителей раунда следующий раунд обновится с получившимися парами',
    howToUseStep3: 'Когда этап завершен, переведите прошедшие команды на следующий этап',
    howToUsePickTitle: 'Выбирайте победителей',
    howToUseSimTitle: 'Пересчитывайте сетку',
    howToUseAdvanceTitle: 'Продвигайте этапы',
    howToUseViewsTitle: 'Переключайте виды',
    howToUseViewsBody: 'Минимальный вид удобен для быстрых выборов, сетка показывает весь путь, классический вид дает плотную таблицу и детали матчей.',
    howToUseLocksTitle: 'Фиксируйте важные выборы',
    howToUseLocksBody: 'Повторный клик по выбранному победителю фиксирует матч, поэтому shuffle и дальнейшие изменения не перезапишут результат.',
    pickemLockScenarioHelp: 'Нажмите выбранного победителя еще раз, чтобы зафиксировать результат матча и пересчитать выживание Pick’em для этого сценария.',
    howToUseStandingsTitle: 'Смотрите контекст таблицы',
    howToUseStandingsBody: 'Buchholz, посев и порядок объясняют, почему команды получают такие пары или места в швейцарских этапах.',
    close: 'Закрыть',
  },
  pl: {
    // eSIM Ad
    supportUs: 'Wesprzyj nas - ',
    esimTitle: 'Uzyskaj eSIM z estońskim numerem, bez opłat miesięcznych',
    esimDescription: 'Działa w 144 krajach • Od €4 • Od autora majors.im',
    esimDescription2: 'Za €33 możesz otrzymać 50GB danych działających w UE, Wielkiej Brytanii, USA, Chinach, Malezji i Tajlandii',
    esimCta: 'Uzyskaj eSIM',
    esimFeatures: 'Bez umów • Bez opłat miesięcznych • Tanie pakiety danych',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'W sprawie reklam/partnerstw biznesowych, skontaktuj się',
    
    // Event Selector
    selectEvent: 'Wybierz wydarzenie',
    languageAuto: 'Auto',
    
    // About Menu
    about: 'O nas',
    majorRulebook: 'Zasady Major',
    majorRulebookSubtitleCS: 'Zasady Valve',
    majorRulebookSubtitleR6: 'Zasady R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Opinie albo zwykła rozmowa',
    kofi: 'Strona Ko-fi',
    kofiSubtitle: 'Wesprzyj tę stronę :D',
    tutorial: 'Samouczek',
    tutorialSubtitle: 'Krótki przewodnik po korzystaniu z tej strony',
    steamProfile: 'Profil Steam',
    steamProfileSubtitle: 'Skomentuj lub daj nagrody profilu',
    
    // UI Types
    classicUI: 'Widok klasyczny',
    bracketUI: 'Widok drabinki',
    minimalUI: 'Widok minimalny',
    
    // Customize
    customize: 'Dostosuj',
    displayMatchesOnly: 'Tylko mecze',
    hideEliminatedTeams: 'Ukryj wyeliminowane drużyny',
    showBestOfIndicator: 'Pokaż format',
    showTeamStandings: 'Pokaż tabelę',
    teamAbbreviations: 'Skróty drużyn',
    themeMode: 'Motyw',
    systemTheme: 'Systemowy',
    lightTheme: 'Jasny',
    darkTheme: 'Ciemny',
    
    // Actions
    advanceToNextStage: 'Przejdź do następnego etapu',
    chooseUIType: 'Widok',
    statusQualified: 'zakwalifikowano',
    statusQuarterfinalist: 'ćwierćfinalista',
    statusSemifinalist: 'półfinalista',
    statusRunnerUp: 'finalista',
    statusChampion: 'mistrz',
    statusEliminated: 'eliminacja',
    statusAdvanced: 'awans',
    finalResults: 'Wyniki końcowe',
    finalResult: 'Wynik końcowy',
    roundLabel: 'Runda',
    
    // How to Use
    howToUse: 'Jak używać',
    howToUseIntro: 'Symulator służy do przewidywań: wybieraj zwycięzców, obserwuj przeliczanie drabinki i porównuj wpływ różnych ścieżek na awans.',
    howToUseStep1: 'Kliknij drużynę, która według Ciebie wygra mecz',
    howToUseStep2: 'Po wybraniu wszystkich zwycięzców rundy następna runda pokaże wynikające z tego pary',
    howToUseStep3: 'Po zakończeniu etapu przenieś awansujące drużyny do następnego etapu',
    howToUsePickTitle: 'Wybieraj zwycięzców',
    howToUseSimTitle: 'Przeliczaj drabinkę',
    howToUseAdvanceTitle: 'Przesuwaj etapy',
    howToUseViewsTitle: 'Zmieniaj widoki',
    howToUseViewsBody: 'Minimalny widok jest do szybkich typów, Drabinka pokazuje pełną ścieżkę, a Klasyczny daje zwartą tabelę i szczegóły.',
    howToUseLocksTitle: 'Blokuj ważne typy',
    howToUseLocksBody: 'Kliknij ponownie wybranego zwycięzcę, aby zablokować mecz; shuffle i późniejsze zmiany nie nadpiszą wyniku.',
    pickemLockScenarioHelp: 'Kliknij ponownie wybranego zwycięzcę, aby zablokować wynik meczu i przeliczyć szanse Pick’em dla tego scenariusza.',
    howToUseStandingsTitle: 'Czytaj kontekst tabeli',
    howToUseStandingsBody: 'Buchholz oraz informacje o seedzie i kolejności wyjaśniają pary oraz miejsca w etapach szwajcarskich.',
    close: 'Zamknij',
  },
  uk: {
    // eSIM Ad
    supportUs: 'Підтримайте нас - ',
    esimTitle: 'Отримайте eSIM з естонським номером, без щомісячної плати',
    esimDescription: 'Працює в 144 країнах • Від €4 • Від автора majors.im',
    esimDescription2: 'За €33 ви можете отримати 50 ГБ даних, працює в ЄС, Великобританії, США, Китаї, Малайзії та Таїланді',
    esimCta: 'Отримати eSIM',
    esimFeatures: 'Без контрактів • Без щомісячної плати • Дешеві тарифи',
    esimSanctions: 'Це не працює в Росії через санкції',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Для реклами/бізнес-партнерства, зв\'яжіться',
    
    // Event Selector
    selectEvent: 'Вибрати подію',
    languageAuto: 'Авто',
    
    // About Menu
    about: 'Про нас',
    majorRulebook: 'Правила Major',
    majorRulebookSubtitleCS: 'Правила Valve',
    majorRulebookSubtitleR6: 'Правила R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Відгуки або просто чат',
    kofi: 'Сторінка Ko-fi',
    kofiSubtitle: 'Пожертвувати на сайт :D',
    tutorial: 'Посібник',
    tutorialSubtitle: 'Короткий посібник з використання сайту',
    steamProfile: 'Профіль Steam',
    steamProfileSubtitle: 'Залишити коментар або нагороду профілю',
    
    // UI Types
    classicUI: 'Класичний вигляд',
    bracketUI: 'Вигляд сітки',
    minimalUI: 'Мінімальний вигляд',
    
    // Customize
    customize: 'Налаштування',
    displayMatchesOnly: 'Тільки матчі',
    hideEliminatedTeams: 'Сховати вибулі команди',
    showBestOfIndicator: 'Показати формат',
    showTeamStandings: 'Показати таблицю',
    teamAbbreviations: 'Скорочення команд',
    themeMode: 'Тема',
    systemTheme: 'Системна',
    lightTheme: 'Світла',
    darkTheme: 'Темна',
    
    // Actions
    advanceToNextStage: 'Перейти до наступного етапу',
    chooseUIType: 'Вигляд',
    statusQualified: 'кваліфікувалася',
    statusQuarterfinalist: 'чвертьфіналіст',
    statusSemifinalist: 'півфіналіст',
    statusRunnerUp: 'фіналіст',
    statusChampion: 'чемпіон',
    statusEliminated: 'вибула',
    statusAdvanced: 'пройшла',
    finalResults: 'Підсумкові результати',
    finalResult: 'Підсумковий результат',
    roundLabel: 'Раунд',
    
    // How to Use
    howToUse: 'Як використовувати',
    howToUseIntro: 'Цей симулятор допомагає прогнозувати: обирайте переможців, дивіться перерахунок сітки й порівнюйте, як різні шляхи впливають на прохід далі.',
    howToUseStep1: 'Натисніть команду, яка, на вашу думку, виграє матч',
    howToUseStep2: 'Після вибору всіх переможців раунду наступний раунд оновиться з новими парами',
    howToUseStep3: 'Коли етап завершено, переведіть команди, що пройшли, до наступного етапу',
    howToUsePickTitle: 'Обирайте переможців',
    howToUseSimTitle: 'Перераховуйте сітку',
    howToUseAdvanceTitle: 'Просувайте етапи',
    howToUseViewsTitle: 'Перемикайте вигляди',
    howToUseViewsBody: 'Мінімальний вигляд підходить для швидких виборів, сітка показує повний шлях, класичний вигляд дає щільну таблицю й деталі.',
    howToUseLocksTitle: 'Фіксуйте важливі вибори',
    howToUseLocksBody: 'Повторний клік по вибраному переможцю фіксує матч, тому shuffle або подальші зміни не перезапишуть результат.',
    pickemLockScenarioHelp: 'Натисніть вибраного переможця ще раз, щоб зафіксувати результат матчу й перерахувати виживання Pick’em для цього сценарію.',
    howToUseStandingsTitle: 'Читайте контекст таблиці',
    howToUseStandingsBody: 'Buchholz, посів і порядок пояснюють, чому команди отримали такі пари або місця у швейцарських етапах.',
    close: 'Закрити',
  },
  de: {
    // eSIM Ad
    supportUs: 'Unterstützen Sie uns - ',
    esimTitle: 'Holen Sie sich eine eSIM mit estnischer Nummer, keine monatliche Gebühr',
    esimDescription: 'Funktioniert in 144 Ländern • Ab €4 • Vom Autor von majors.im',
    esimDescription2: 'Für €33 erhalten Sie 50GB Daten, funktioniert in EU, UK, USA, China, Malaysia und Thailand',
    esimCta: 'eSIM erhalten',
    esimFeatures: 'Keine Verträge • Keine monatlichen Gebühren • Günstige Datenpakete',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Für Werbung/Geschäftspartnerschaften, kontaktieren Sie',
    
    // Event Selector
    selectEvent: 'Event auswählen',
    languageAuto: 'Automatisch',
    
    // About Menu
    about: 'Über',
    majorRulebook: 'Major-Regelwerk',
    majorRulebookSubtitleCS: 'Das Valve-Regelwerk',
    majorRulebookSubtitleR6: 'R6 Major-Regeln',
    discord: 'Discord',
    discordSubtitle: 'Feedback oder einfach chatten',
    kofi: 'Ko-fi-Seite',
    kofiSubtitle: 'Spenden Sie für diese Seite :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'Eine kurze Anleitung zur Nutzung dieser Seite',
    steamProfile: 'Steam-Profil',
    steamProfileSubtitle: 'Kommentieren oder Profilauszeichnungen geben',
    
    // UI Types
    classicUI: 'Klassische Ansicht',
    bracketUI: 'Turnierbaum-Ansicht',
    minimalUI: 'Minimale Ansicht',
    
    // Customize
    customize: 'Anpassen',
    displayMatchesOnly: 'Nur Spiele',
    hideEliminatedTeams: 'Ausgeschiedene Teams ausblenden',
    showBestOfIndicator: 'Format anzeigen',
    showTeamStandings: 'Tabelle anzeigen',
    teamAbbreviations: 'Team-Abkürzungen',
    themeMode: 'Theme',
    systemTheme: 'System',
    lightTheme: 'Hell',
    darkTheme: 'Dunkel',
    
    // Actions
    advanceToNextStage: 'Zur nächsten Phase',
    chooseUIType: 'Ansicht',
    statusQualified: 'qualifiziert',
    statusQuarterfinalist: 'Viertelfinalist',
    statusSemifinalist: 'Halbfinalist',
    statusRunnerUp: 'Finalist',
    statusChampion: 'Champion',
    statusEliminated: 'ausgeschieden',
    statusAdvanced: 'weiter',
    finalResults: 'Endergebnisse',
    finalResult: 'Endergebnis',
    roundLabel: 'Runde',
    
    // How to Use
    howToUse: 'Wie man verwendet',
    howToUseIntro: 'Der Simulator ist ein Prognosewerkzeug: Gewinner wählen, die Berechnung der Paarungen verfolgen und verschiedene Wege zum Weiterkommen vergleichen.',
    howToUseStep1: 'Klicken Sie auf das Team, das Ihrer Meinung nach das Spiel gewinnt',
    howToUseStep2: 'Nachdem alle Gewinner einer Runde gewählt wurden, aktualisiert sich die nächste Runde mit den daraus entstehenden Paarungen',
    howToUseStep3: 'Wenn eine Phase abgeschlossen ist, bringen Sie die qualifizierten Teams in die nächste Phase',
    howToUsePickTitle: 'Gewinner wählen',
    howToUseSimTitle: 'Turnierbaum neu berechnen',
    howToUseAdvanceTitle: 'Phasen weiterführen',
    howToUseViewsTitle: 'Ansichten wechseln',
    howToUseViewsBody: 'Minimal eignet sich für schnelle Tipps, Turnierbaum zeigt den ganzen Pfad, Klassisch bietet kompakte Tabellen und Matchdetails.',
    howToUseLocksTitle: 'Wichtige Tipps sperren',
    howToUseLocksBody: 'Klicken Sie den aktuell gewählten Gewinner erneut, um das Match zu sperren; Shuffle und spätere Änderungen behalten das Ergebnis bei.',
    pickemLockScenarioHelp: 'Klicken Sie den gewählten Gewinner erneut, um das Ergebnis zu fixieren und die Pick’em-Überlebenschance für dieses Szenario neu zu berechnen.',
    howToUseStandingsTitle: 'Tabellenkontext lesen',
    howToUseStandingsBody: 'Buchholz sowie Seed- und Reihenfolgenhinweise erklären Paarungen und Ranglisten in Swiss-Phasen.',
    close: 'Schließen',
  },
  fr: {
    // eSIM Ad
    supportUs: 'Soutenez-nous - ',
    esimTitle: 'Obtenez une eSIM avec un numéro estonien, sans frais mensuels',
    esimDescription: 'Fonctionne dans 144 pays • À partir de €4 • Par l\'auteur de majors.im',
    esimDescription2: 'Pour €33, vous pouvez obtenir 50 Go de données fonctionnant dans l\'UE, le Royaume-Uni, les États-Unis, la Chine, la Malaisie et la Thaïlande',
    esimCta: 'Obtenir eSIM',
    esimFeatures: 'Sans contrat • Sans frais mensuels • Forfaits pas chers',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Pour la publicité/partenariats commerciaux, contactez',
    
    // Event Selector
    selectEvent: 'Sélectionner un événement',
    languageAuto: 'Auto',
    
    // About Menu
    about: 'À propos',
    majorRulebook: 'Règlement Major',
    majorRulebookSubtitleCS: 'Le règlement Valve',
    majorRulebookSubtitleR6: 'Règles R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Retours ou simple discussion',
    kofi: 'Page Ko-fi',
    kofiSubtitle: 'Faire un don à ce site :D',
    tutorial: 'Tutoriel',
    tutorialSubtitle: 'Un guide rapide pour utiliser ce site',
    steamProfile: 'Profil Steam',
    steamProfileSubtitle: 'Commenter ou donner des récompenses de profil',
    
    // UI Types
    classicUI: 'Vue classique',
    bracketUI: 'Vue tableau',
    minimalUI: 'Vue minimale',
    
    // Customize
    customize: 'Personnaliser',
    displayMatchesOnly: 'Matchs uniquement',
    hideEliminatedTeams: 'Masquer les équipes éliminées',
    showBestOfIndicator: 'Afficher le format',
    showTeamStandings: 'Afficher le classement',
    teamAbbreviations: 'Abréviations des équipes',
    themeMode: 'Thème',
    systemTheme: 'Système',
    lightTheme: 'Clair',
    darkTheme: 'Sombre',
    
    // Actions
    advanceToNextStage: 'Passer à la phase suivante',
    chooseUIType: 'Vue',
    statusQualified: 'qualifiée',
    statusQuarterfinalist: 'quart de finaliste',
    statusSemifinalist: 'demi-finaliste',
    statusRunnerUp: 'finaliste',
    statusChampion: 'championne',
    statusEliminated: 'éliminée',
    statusAdvanced: 'qualifiée',
    finalResults: 'Résultats finaux',
    finalResult: 'Résultat final',
    roundLabel: 'Ronde',
    
    // How to Use
    howToUse: 'Comment utiliser',
    howToUseIntro: 'Ce simulateur sert à prévoir: choisissez les vainqueurs, laissez le tableau se recalculer et comparez l’effet des différents parcours.',
    howToUseStep1: 'Cliquez sur l’équipe que vous pensez voir gagner chaque match',
    howToUseStep2: 'Après avoir choisi tous les vainqueurs d’une ronde, la ronde suivante se met à jour avec les confrontations obtenues',
    howToUseStep3: 'Quand une phase est terminée, envoyez les équipes qualifiées vers la phase suivante',
    howToUsePickTitle: 'Choisir les vainqueurs',
    howToUseSimTitle: 'Recalculer le tableau',
    howToUseAdvanceTitle: 'Faire avancer les phases',
    howToUseViewsTitle: 'Changer de vue',
    howToUseViewsBody: 'Utilisez Minimal pour choisir vite, Tableau pour voir tout le parcours et Classique pour les classements denses et les détails.',
    howToUseLocksTitle: 'Verrouiller les choix importants',
    howToUseLocksBody: 'Cliquez à nouveau sur le vainqueur sélectionné pour verrouiller le match afin que le shuffle ou les changements ne l’écrasent pas.',
    pickemLockScenarioHelp: 'Cliquez à nouveau sur le vainqueur sélectionné pour fixer le résultat et recalculer la survie Pick’em pour ce scénario.',
    howToUseStandingsTitle: 'Lire le contexte du classement',
    howToUseStandingsBody: 'Buchholz, seed et ordre expliquent pourquoi les équipes sont appariées ou classées ainsi dans les phases suisses.',
    close: 'Fermer',
  },
  tr: {
    // eSIM Ad
    supportUs: 'Bizi destekleyin - ',
    esimTitle: 'Estonya numaralı eSIM alın, aylık ücret yok',
    esimDescription: '144 ülkede çalışır • €4\'ten başlayan fiyatlarla • majors.im yazarından',
    esimDescription2: '€33 karşılığında AB, İngiltere, ABD, Çin, Malezya ve Tayland\'da çalışan 50GB veri alabilirsiniz',
    esimCta: 'eSIM Al',
    esimFeatures: 'Sözleşme yok • Aylık ücret yok • Ucuz veri paketleri',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Reklam/iş ortaklıkları için, iletişime geçin',
    
    // Event Selector
    selectEvent: 'Etkinlik Seç',
    languageAuto: 'Otomatik',
    
    // About Menu
    about: 'Hakkında',
    majorRulebook: 'Major Kural Kitabı',
    majorRulebookSubtitleCS: 'Valve Kural Kitabı',
    majorRulebookSubtitleR6: 'R6 Major Kuralları',
    discord: 'Discord',
    discordSubtitle: 'Geri bildirim veya sadece sohbet',
    kofi: 'Ko-fi Sayfası',
    kofiSubtitle: 'Bu siteye bağış yap :D',
    tutorial: 'Öğretici',
    tutorialSubtitle: 'Bu siteyi kullanmak için kısa bir rehber',
    steamProfile: 'Steam Profili',
    steamProfileSubtitle: 'Yorum yap veya profil ödülü ver',
    
    // UI Types
    classicUI: 'Klasik görünüm',
    bracketUI: 'Eşleşme ağacı',
    minimalUI: 'Minimal görünüm',
    
    // Customize
    customize: 'Özelleştir',
    displayMatchesOnly: 'Sadece maçlar',
    hideEliminatedTeams: 'Elenen takımları gizle',
    showBestOfIndicator: 'Formatı göster',
    showTeamStandings: 'Sıralamayı göster',
    teamAbbreviations: 'Takım kısaltmaları',
    themeMode: 'Tema',
    systemTheme: 'Sistem',
    lightTheme: 'Açık',
    darkTheme: 'Koyu',
    
    // Actions
    advanceToNextStage: 'Sonraki aşamaya geç',
    chooseUIType: 'Görünüm',
    statusQualified: 'katıldı',
    statusQuarterfinalist: 'çeyrek finalist',
    statusSemifinalist: 'yarı finalist',
    statusRunnerUp: 'finalist',
    statusChampion: 'şampiyon',
    statusEliminated: 'elendi',
    statusAdvanced: 'ilerledi',
    finalResults: 'Final sonuçları',
    finalResult: 'Final sonucu',
    roundLabel: 'Tur',
    
    // How to Use
    howToUse: 'Nasıl Kullanılır',
    howToUseIntro: 'Bu simülatör tahmin içindir: kazananları seçin, eşleşmelerin yeniden hesaplanmasını izleyin ve farklı yolların ilerlemeye etkisini karşılaştırın.',
    howToUseStep1: 'Her maçta kazanacağını düşündüğünüz takıma tıklayın',
    howToUseStep2: 'Bir turun tüm kazananlarını seçtikten sonra sonraki tur ortaya çıkan eşleşmelerle güncellenir',
    howToUseStep3: 'Aşama tamamlandığında, çıkan takımları sonraki aşamaya taşıyın',
    howToUsePickTitle: 'Kazananları seç',
    howToUseSimTitle: 'Eşleşmeleri yeniden hesapla',
    howToUseAdvanceTitle: 'Aşamaları ilerlet',
    howToUseViewsTitle: 'Görünümleri değiştir',
    howToUseViewsBody: 'Hızlı seçim için Minimal, tüm yolu görmek için Eşleşme ağacı, yoğun sıralama ve detay için Klasik görünümü kullanın.',
    howToUseLocksTitle: 'Önemli seçimleri kilitle',
    howToUseLocksBody: 'Seçili kazanana tekrar tıklamak maçı kilitler; shuffle veya sonraki değişiklikler bu sonucu değiştirmez.',
    pickemLockScenarioHelp: 'Seçili kazanana tekrar tıklayarak maç sonucunu sabitleyin ve bu senaryo için Pick’em hayatta kalma oranını yeniden hesaplayın.',
    howToUseStandingsTitle: 'Sıralama bağlamını oku',
    howToUseStandingsBody: 'Buchholz, seed ve sıra notları İsviçre aşamalarında eşleşme ve sıralama nedenlerini açıklar.',
    close: 'Kapat',
  },
  sv: {
    // eSIM Ad
    supportUs: 'Stödj oss - ',
    esimTitle: 'Få en eSIM med estniskt nummer, ingen månadsavgift',
    esimDescription: 'Fungerar i 144 länder • Från €4 • Från författaren av majors.im',
    esimDescription2: 'För €33 kan du få 50GB data som fungerar i EU, Storbritannien, USA, Kina, Malaysia och Thailand',
    esimCta: 'Få eSIM',
    esimFeatures: 'Inga kontrakt • Inga månadsavgifter • Billiga datapaket',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'För annonser/affärspartnerskap, kontakta',
    
    // Event Selector
    selectEvent: 'Välj evenemang',
    languageAuto: 'Automatiskt',
    
    // About Menu
    about: 'Om',
    majorRulebook: 'Major-regelbok',
    majorRulebookSubtitleCS: 'Valve-regelboken',
    majorRulebookSubtitleR6: 'R6 Major-regler',
    discord: 'Discord',
    discordSubtitle: 'Feedback eller bara chatt',
    kofi: 'Ko-fi-sida',
    kofiSubtitle: 'Donera till denna sida :D',
    tutorial: 'Handledning',
    tutorialSubtitle: 'En snabb guide till hur sidan används',
    steamProfile: 'Steam-profil',
    steamProfileSubtitle: 'Kommentera eller ge profilutmärkelser',
    
    // UI Types
    classicUI: 'Klassisk vy',
    bracketUI: 'Slutspelsvy',
    minimalUI: 'Minimal vy',
    
    // Customize
    customize: 'Anpassa',
    displayMatchesOnly: 'Endast matcher',
    hideEliminatedTeams: 'Dölj utslagna lag',
    showBestOfIndicator: 'Visa format',
    showTeamStandings: 'Visa tabell',
    teamAbbreviations: 'Lagförkortningar',
    themeMode: 'Tema',
    systemTheme: 'System',
    lightTheme: 'Ljus',
    darkTheme: 'Mörk',
    
    // Actions
    advanceToNextStage: 'Gå till nästa steg',
    chooseUIType: 'Vy',
    statusQualified: 'kvalificerad',
    statusQuarterfinalist: 'kvartsfinalist',
    statusSemifinalist: 'semifinalist',
    statusRunnerUp: 'finalist',
    statusChampion: 'mästare',
    statusEliminated: 'utslagen',
    statusAdvanced: 'vidare',
    finalResults: 'Slutresultat',
    finalResult: 'Slutresultat',
    roundLabel: 'Omgång',
    
    // How to Use
    howToUse: 'Hur man använder',
    howToUseIntro: 'Simulatorn är ett prognosverktyg: välj vinnare, se bracketen räknas om och jämför hur olika vägar påverkar avancemang.',
    howToUseStep1: 'Klicka på laget du tror vinner varje match',
    howToUseStep2: 'När alla vinnare i en omgång har valts uppdateras nästa omgång med de nya matcherna',
    howToUseStep3: 'När en fas är klar flyttar du de kvalificerade lagen till nästa fas',
    howToUsePickTitle: 'Välj vinnare',
    howToUseSimTitle: 'Räkna om bracketen',
    howToUseAdvanceTitle: 'Flytta fram faser',
    howToUseViewsTitle: 'Byt vy',
    howToUseViewsBody: 'Använd Minimal för snabba val, Slutspelsvy för hela vägen och Klassisk för kompakt tabell och matchdetaljer.',
    howToUseLocksTitle: 'Lås viktiga val',
    howToUseLocksBody: 'Klicka på vald vinnare igen för att låsa matchen, så behåller shuffle och senare ändringar resultatet.',
    pickemLockScenarioHelp: 'Klicka på den valda vinnaren igen för att låsa matchresultatet och räkna om Pick’em-chansen för det scenariot.',
    howToUseStandingsTitle: 'Läs tabellkontext',
    howToUseStandingsBody: 'Buchholz samt seed- och ordningsnoter förklarar parningar och placeringar i Swiss-faser.',
    close: 'Stäng',
  },
  nl: {
    // eSIM Ad
    supportUs: 'Steun ons - ',
    esimTitle: 'Krijg een eSIM met Ests nummer, geen maandelijkse kosten',
    esimDescription: 'Werkt in 144 landen • Vanaf €4 • Van de auteur van majors.im',
    esimDescription2: 'Voor €33 kun je 50GB data krijgen die werkt in EU, VK, VS, China, Maleisië en Thailand',
    esimCta: 'Krijg eSIM',
    esimFeatures: 'Geen contracten • Geen maandelijkse kosten • Goedkope datapakketten',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Voor advertenties/zakelijke partnerschappen, neem contact op',
    
    // Event Selector
    selectEvent: 'Selecteer evenement',
    languageAuto: 'Automatisch',
    
    // About Menu
    about: 'Over',
    majorRulebook: 'Major-regelboek',
    majorRulebookSubtitleCS: 'Het Valve-regelboek',
    majorRulebookSubtitleR6: 'R6 Major-regels',
    discord: 'Discord',
    discordSubtitle: 'Feedback of gewoon chatten',
    kofi: 'Ko-fi-pagina',
    kofiSubtitle: 'Doneer aan deze site :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'Een korte gids voor het gebruik van deze site',
    steamProfile: 'Steam-profiel',
    steamProfileSubtitle: 'Reageren of profielprijzen geven',
    
    // UI Types
    classicUI: 'Klassieke weergave',
    bracketUI: 'Schemaweergave',
    minimalUI: 'Minimale weergave',
    
    // Customize
    customize: 'Aanpassen',
    displayMatchesOnly: 'Alleen wedstrijden',
    hideEliminatedTeams: 'Uitgeschakelde teams verbergen',
    showBestOfIndicator: 'Format tonen',
    showTeamStandings: 'Stand tonen',
    teamAbbreviations: 'Teamafkortingen',
    themeMode: 'Thema',
    systemTheme: 'Systeem',
    lightTheme: 'Licht',
    darkTheme: 'Donker',
    
    // Actions
    advanceToNextStage: 'Ga naar de volgende fase',
    chooseUIType: 'Weergave',
    statusQualified: 'gekwalificeerd',
    statusQuarterfinalist: 'kwartfinalist',
    statusSemifinalist: 'halvefinalist',
    statusRunnerUp: 'finalist',
    statusChampion: 'kampioen',
    statusEliminated: 'uitgeschakeld',
    statusAdvanced: 'door',
    finalResults: 'Eindresultaten',
    finalResult: 'Eindresultaat',
    roundLabel: 'Ronde',
    
    // How to Use
    howToUse: 'Hoe te gebruiken',
    howToUseIntro: 'Deze simulator is een voorspellingstool: kies winnaars, zie het schema opnieuw berekenen en vergelijk hoe routes plaatsing beïnvloeden.',
    howToUseStep1: 'Klik op het team waarvan je verwacht dat het de wedstrijd wint',
    howToUseStep2: 'Nadat alle winnaars van een ronde zijn gekozen, wordt de volgende ronde bijgewerkt met de nieuwe wedstrijden',
    howToUseStep3: 'Wanneer een fase klaar is, zet je de geplaatste teams door naar de volgende fase',
    howToUsePickTitle: 'Kies winnaars',
    howToUseSimTitle: 'Herbereken het schema',
    howToUseAdvanceTitle: 'Ga fases door',
    howToUseViewsTitle: 'Wissel van weergave',
    howToUseViewsBody: 'Gebruik Minimaal voor snelle keuzes, Schema voor het volledige pad en Klassiek voor compacte standen en wedstrijddetails.',
    howToUseLocksTitle: 'Vergrendel belangrijke keuzes',
    howToUseLocksBody: 'Klik opnieuw op de gekozen winnaar om de wedstrijd te vergrendelen; shuffle of latere wijzigingen behouden dat resultaat.',
    pickemLockScenarioHelp: 'Klik opnieuw op je gekozen winnaar om de uitslag vast te zetten en de Pick’em-overlevingskans voor dat scenario opnieuw te berekenen.',
    howToUseStandingsTitle: 'Lees standcontext',
    howToUseStandingsBody: 'Buchholz en seed-/volgordenotities verklaren paringen en posities in Swiss-fases.',
    close: 'Sluiten',
  },
  cs: {
    // eSIM Ad
    supportUs: 'Podpořte nás - ',
    esimTitle: 'Získejte eSIM s estonským číslem, bez měsíčních poplatků',
    esimDescription: 'Funguje ve 144 zemích • Od €4 • Od autora majors.im',
    esimDescription2: 'Za €33 můžete získat 50GB dat, které fungují v EU, UK, USA, Číně, Malajsii a Thajsku',
    esimCta: 'Získat eSIM',
    esimFeatures: 'Bez smluv • Bez měsíčních poplatků • Levné datové balíčky',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Pro reklamu/obchodní partnerství, kontaktujte',
    
    // Event Selector
    selectEvent: 'Vybrat událost',
    languageAuto: 'Automaticky',
    
    // About Menu
    about: 'O nás',
    majorRulebook: 'Pravidla Major',
    majorRulebookSubtitleCS: 'Pravidla Valve',
    majorRulebookSubtitleR6: 'Pravidla R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Zpětná vazba nebo jen chat',
    kofi: 'Stránka Ko-fi',
    kofiSubtitle: 'Přispět na tento web :D',
    tutorial: 'Návod',
    tutorialSubtitle: 'Krátký průvodce používáním tohoto webu',
    steamProfile: 'Steam profil',
    steamProfileSubtitle: 'Komentovat nebo dát ocenění profilu',
    
    // UI Types
    classicUI: 'Klasické zobrazení',
    bracketUI: 'Zobrazení pavouka',
    minimalUI: 'Minimální zobrazení',
    
    // Customize
    customize: 'Přizpůsobit',
    displayMatchesOnly: 'Pouze zápasy',
    hideEliminatedTeams: 'Skrýt vyřazené týmy',
    showBestOfIndicator: 'Zobrazit formát',
    showTeamStandings: 'Zobrazit tabulku',
    teamAbbreviations: 'Zkratky týmů',
    themeMode: 'Motiv',
    systemTheme: 'Systémový',
    lightTheme: 'Světlý',
    darkTheme: 'Tmavý',
    
    // Actions
    advanceToNextStage: 'Přejít do další fáze',
    chooseUIType: 'Zobrazení',
    statusQualified: 'kvalifikováno',
    statusQuarterfinalist: 'čtvrtfinalista',
    statusSemifinalist: 'semifinalista',
    statusRunnerUp: 'finalista',
    statusChampion: 'šampion',
    statusEliminated: 'vyřazeno',
    statusAdvanced: 'postup',
    finalResults: 'Konečné výsledky',
    finalResult: 'Konečný výsledek',
    roundLabel: 'Kolo',
    
    // How to Use
    howToUse: 'Jak používat',
    howToUseIntro: 'Simulátor slouží k predikcím: vybírejte vítěze, sledujte přepočet pavouka a porovnávejte dopad různých cest na postup.',
    howToUseStep1: 'Klikněte na tým, který podle vás vyhraje zápas',
    howToUseStep2: 'Po výběru všech vítězů kola se další kolo aktualizuje o vzniklé dvojice',
    howToUseStep3: 'Po dokončení fáze posuňte postupující týmy do další fáze',
    howToUsePickTitle: 'Vyberte vítěze',
    howToUseSimTitle: 'Přepočítat pavouka',
    howToUseAdvanceTitle: 'Posouvat fáze',
    howToUseViewsTitle: 'Přepínat zobrazení',
    howToUseViewsBody: 'Minimální zobrazení je pro rychlé tipy, Pavouk pro celou cestu a Klasické pro kompaktní tabulku a detaily.',
    howToUseLocksTitle: 'Uzamknout důležité tipy',
    howToUseLocksBody: 'Kliknutí na již vybraného vítěze uzamkne zápas, takže shuffle ani pozdější změny výsledek nepřepíšou.',
    pickemLockScenarioHelp: 'Klikněte znovu na vybraného vítěze, tím uzamknete výsledek zápasu a přepočítáte šanci Pick’em pro tento scénář.',
    howToUseStandingsTitle: 'Číst kontext tabulky',
    howToUseStandingsBody: 'Buchholz a poznámky k seedu/pořadí vysvětlují párování a pořadí ve švýcarských fázích.',
    close: 'Zavřít',
  },
  fi: {
    // eSIM Ad
    supportUs: 'Tue meitä - ',
    esimTitle: 'Hanki eSIM virolaisella numerolla, ei kuukausimaksua',
    esimDescription: 'Toimii 144 maassa • Alkaen €4 • Majors.im:n tekijältä',
    esimDescription2: '€33:lla saat 50GB tietoja, joka toimii EU:ssa, Isossa-Britanniassa, Yhdysvalloissa, Kiinassa, Malesiassa ja Thaimaassa',
    esimCta: 'Hanki eSIM',
    esimFeatures: 'Ei sopimuksia • Ei kuukausimaksuja • Halvat datapaketit',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Mainoksiin/liiketoimintakumppanuuksiin, ota yhteyttä',
    
    // Event Selector
    selectEvent: 'Valitse tapahtuma',
    languageAuto: 'Automaattinen',
    
    // About Menu
    about: 'Tietoja',
    majorRulebook: 'Major-sääntökirja',
    majorRulebookSubtitleCS: 'Valven sääntökirja',
    majorRulebookSubtitleR6: 'R6 Major-säännöt',
    discord: 'Discord',
    discordSubtitle: 'Palautetta tai vain jutustelua',
    kofi: 'Ko-fi-sivu',
    kofiSubtitle: 'Lahjoita tälle sivustolle :D',
    tutorial: 'Opas',
    tutorialSubtitle: 'Lyhyt opas sivuston käyttöön',
    steamProfile: 'Steam-profiili',
    steamProfileSubtitle: 'Kommentoi tai anna profiilipalkintoja',
    
    // UI Types
    classicUI: 'Klassinen näkymä',
    bracketUI: 'Kaavionäkymä',
    minimalUI: 'Minimaalinen näkymä',
    
    // Customize
    customize: 'Mukauta',
    displayMatchesOnly: 'Vain ottelut',
    hideEliminatedTeams: 'Piilota pudonneet joukkueet',
    showBestOfIndicator: 'Näytä formaatti',
    showTeamStandings: 'Näytä sijoitukset',
    teamAbbreviations: 'Joukkueiden lyhenteet',
    themeMode: 'Teema',
    systemTheme: 'Järjestelmä',
    lightTheme: 'Vaalea',
    darkTheme: 'Tumma',
    
    // Actions
    advanceToNextStage: 'Siirry seuraavaan vaiheeseen',
    chooseUIType: 'Näkymä',
    statusQualified: 'karsittu jatkoon',
    statusQuarterfinalist: 'puolivälieräjoukkue',
    statusSemifinalist: 'semifinalisti',
    statusRunnerUp: 'finalisti',
    statusChampion: 'mestari',
    statusEliminated: 'pudonnut',
    statusAdvanced: 'jatkoon',
    finalResults: 'Lopputulokset',
    finalResult: 'Lopputulos',
    roundLabel: 'Kierros',
    
    // How to Use
    howToUse: 'Kuinka käyttää',
    howToUseIntro: 'Simulaattori on ennustetyökalu: valitse voittajat, seuraa kaavion uudelleenlaskentaa ja vertaa eri polkujen vaikutusta jatkoonpääsyyn.',
    howToUseStep1: 'Napsauta joukkuetta, jonka odotat voittavan ottelun',
    howToUseStep2: 'Kun kaikki kierroksen voittajat on valittu, seuraava kierros päivittyy syntyneillä ottelupareilla',
    howToUseStep3: 'Kun vaihe on valmis, siirrä jatkoon päässeet joukkueet seuraavaan vaiheeseen',
    howToUsePickTitle: 'Valitse voittajat',
    howToUseSimTitle: 'Laske kaavio uudelleen',
    howToUseAdvanceTitle: 'Vie vaiheet eteenpäin',
    howToUseViewsTitle: 'Vaihda näkymää',
    howToUseViewsBody: 'Käytä Minimaalista nopeisiin valintoihin, Kaaviota koko polkuun ja Klassista tiiviisiin sijoituksiin ja yksityiskohtiin.',
    howToUseLocksTitle: 'Lukitse tärkeät valinnat',
    howToUseLocksBody: 'Napsauta valittua voittajaa uudelleen lukitaksesi ottelun, jolloin shuffle tai myöhemmät muutokset eivät korvaa tulosta.',
    pickemLockScenarioHelp: 'Napsauta valittua voittajaa uudelleen lukitaksesi ottelutuloksen ja laskeaksesi Pick’em-selviytymisen tälle skenaariolle.',
    howToUseStandingsTitle: 'Lue sijoitusten taustat',
    howToUseStandingsBody: 'Buchholz sekä seed- ja järjestysmerkinnät selittävät parit ja sijoitukset Swiss-vaiheissa.',
    close: 'Sulje',
  },
  ro: {
    // eSIM Ad
    supportUs: 'Sprijiniți-ne - ',
    esimTitle: 'Obțineți un eSIM cu număr estonian, fără taxă lunară',
    esimDescription: 'Funcționează în 144 de țări • De la €4 • De la autorul majors.im',
    esimDescription2: 'Pentru €33 poți obține 50GB de date care funcționează în UE, Marea Britanie, SUA, China, Malaezia și Thailanda',
    esimCta: 'Obține eSIM',
    esimFeatures: 'Fără contracte • Fără taxe lunare • Pachete de date ieftine',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Pentru publicitate/parteneriate de afaceri, contactați',
    
    // Event Selector
    selectEvent: 'Selectează eveniment',
    languageAuto: 'Automat',
    
    // About Menu
    about: 'Despre',
    majorRulebook: 'Regulamentul Major',
    majorRulebookSubtitleCS: 'Regulamentul Valve',
    majorRulebookSubtitleR6: 'Regulile R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Feedback sau doar chat',
    kofi: 'Pagina Ko-fi',
    kofiSubtitle: 'Donează acestui site :D',
    tutorial: 'Tutorial',
    tutorialSubtitle: 'Un ghid rapid pentru folosirea site-ului',
    steamProfile: 'Profil Steam',
    steamProfileSubtitle: 'Comentează sau oferă premii de profil',
    
    // UI Types
    classicUI: 'Vizualizare clasică',
    bracketUI: 'Vizualizare tablou',
    minimalUI: 'Vizualizare minimă',
    
    // Customize
    customize: 'Personalizează',
    displayMatchesOnly: 'Doar meciuri',
    hideEliminatedTeams: 'Ascunde echipele eliminate',
    showBestOfIndicator: 'Afișează formatul',
    showTeamStandings: 'Afișează clasamentul',
    teamAbbreviations: 'Abrevieri echipe',
    themeMode: 'Temă',
    systemTheme: 'Sistem',
    lightTheme: 'Deschis',
    darkTheme: 'Închis',
    
    // Actions
    advanceToNextStage: 'Avansează la etapa următoare',
    chooseUIType: 'Vizualizare',
    statusQualified: 'calificată',
    statusQuarterfinalist: 'sfertfinalistă',
    statusSemifinalist: 'semifinalistă',
    statusRunnerUp: 'finalistă',
    statusChampion: 'campioană',
    statusEliminated: 'eliminată',
    statusAdvanced: 'calificată',
    finalResults: 'Rezultate finale',
    finalResult: 'Rezultat final',
    roundLabel: 'Runda',
    
    // How to Use
    howToUse: 'Cum se folosește',
    howToUseIntro: 'Simulatorul este un instrument de predicție: alege câștigători, urmărește recalcularea tabloului și compară efectul diferitelor parcursuri.',
    howToUseStep1: 'Dați clic pe echipa pe care o așteptați să câștige fiecare meci',
    howToUseStep2: 'După alegerea tuturor câștigătorilor unei runde, runda următoare se actualizează cu meciurile rezultate',
    howToUseStep3: 'Când o etapă este completă, trimiteți echipele calificate în etapa următoare',
    howToUsePickTitle: 'Alege câștigători',
    howToUseSimTitle: 'Recalculează tabloul',
    howToUseAdvanceTitle: 'Avansează etapele',
    howToUseViewsTitle: 'Schimbă vizualizarea',
    howToUseViewsBody: 'Folosește Minimă pentru alegeri rapide, Tablou pentru traseul complet și Clasică pentru clasamente compacte și detalii.',
    howToUseLocksTitle: 'Blochează alegeri importante',
    howToUseLocksBody: 'Un clic din nou pe câștigătorul selectat blochează meciul, astfel încât shuffle sau schimbările ulterioare păstrează rezultatul.',
    pickemLockScenarioHelp: 'Dă clic din nou pe câștigătorul selectat pentru a fixa rezultatul și a recalcula supraviețuirea Pick’em pentru acel scenariu.',
    howToUseStandingsTitle: 'Citește contextul clasamentului',
    howToUseStandingsBody: 'Buchholz și notele despre seed/ordine explică împerecherile și clasările din etapele Swiss.',
    close: 'Închide',
  },
  mn: {
    // eSIM Ad
    supportUs: 'Биднийг дэмж - ',
    esimTitle: 'Эстонийн дугаартай eSIM авах, сарын төлбөргүй',
    esimDescription: '144 улсад ажилладаг • €4-өөс эхлэн • majors.im зохиогчоос',
    esimDescription2: '€33-оор та 50GB өгөгдөл авах боломжтой, ЕХ, Их Британи, АНУ, Хятад, Малайз, Тайланд улсуудад ажиллана',
    esimCta: 'eSIM авах',
    esimFeatures: 'Гэрээгүй • Сарын төлбөргүй • Хямд өгөгдлийн багц',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Зар сурталчилгаа/бизнес түншлэлийн хувьд, холбогдох',
    
    // Event Selector
    selectEvent: 'Үйл явдал сонгох',
    languageAuto: 'Автомат',
    
    // About Menu
    about: 'Бидний тухай',
    majorRulebook: 'Major дүрэм',
    majorRulebookSubtitleCS: 'Valve дүрэм',
    majorRulebookSubtitleR6: 'R6 Major дүрэм',
    discord: 'Discord',
    discordSubtitle: 'Санал хүсэлт эсвэл энгийн чат',
    kofi: 'Ko-fi хуудас',
    kofiSubtitle: 'Энэ сайтад хандив өгөх :D',
    tutorial: 'Заавар',
    tutorialSubtitle: 'Энэ сайтыг ашиглах товч заавар',
    steamProfile: 'Steam профайл',
    steamProfileSubtitle: 'Сэтгэгдэл бичих эсвэл профайлын шагнал өгөх',
    
    // UI Types
    classicUI: 'Сонгодог харагдац',
    bracketUI: 'Хүснэгтийн харагдац',
    minimalUI: 'Энгийн харагдац',
    
    // Customize
    customize: 'Тохируулах',
    displayMatchesOnly: 'Зөвхөн тоглолтууд',
    hideEliminatedTeams: 'Хасагдсан багуудыг нуух',
    showBestOfIndicator: 'Формат харуулах',
    showTeamStandings: 'Байрлал харуулах',
    teamAbbreviations: 'Багийн товчлол',
    themeMode: 'Загвар',
    systemTheme: 'Систем',
    lightTheme: 'Гэрэл',
    darkTheme: 'Харанхуй',
    
    // Actions
    advanceToNextStage: 'Дараагийн шат руу оруулах',
    chooseUIType: 'Харагдац',
    statusQualified: 'эрх авсан',
    statusQuarterfinalist: 'шөвгийн найм',
    statusSemifinalist: 'хагас шигшээ',
    statusRunnerUp: 'финалист',
    statusChampion: 'аварга',
    statusEliminated: 'хасагдсан',
    statusAdvanced: 'шалгарсан',
    finalResults: 'Эцсийн дүн',
    finalResult: 'Эцсийн дүн',
    roundLabel: 'Тойрог',
    
    // How to Use
    howToUse: 'Хэрхэн ашиглах',
    howToUseIntro: 'Энэ симулятор нь таамаглах хэрэгсэл: ялагчдыг сонгож, хүснэгт дахин тооцоолохыг харж, өөр замууд шалгаралтад хэрхэн нөлөөлөхийг харьцуулна.',
    howToUseStep1: 'Тоглолт бүрт хожно гэж бодож буй багаа дарна уу',
    howToUseStep2: 'Нэг тойргийн бүх ялагчийг сонгосны дараа дараагийн тойрог шинэ хослолуудаар шинэчлэгдэнэ',
    howToUseStep3: 'Шат дуусахад шалгарсан багуудыг дараагийн шат руу шилжүүлнэ',
    howToUsePickTitle: 'Ялагчдыг сонгох',
    howToUseSimTitle: 'Хүснэгтийг дахин тооцоолох',
    howToUseAdvanceTitle: 'Шатуудыг урагшлуулах',
    howToUseViewsTitle: 'Харагдац солих',
    howToUseViewsBody: 'Хурдан сонголтод Энгийн, бүх замыг харахад Хүснэгтийн, нягт байрлал ба дэлгэрэнгүйд Сонгодог харагдацыг ашигла.',
    howToUseLocksTitle: 'Чухал сонголтыг түгжих',
    howToUseLocksBody: 'Сонгосон ялагч дээр дахин дарвал тоглолт түгжигдэж, shuffle эсвэл дараагийн өөрчлөлт үр дүнг солихгүй.',
    pickemLockScenarioHelp: 'Сонгосон ялагч дээрээ дахин дарж тоглолтын үр дүнг түгжээд, тухайн нөхцөл дэх Pick’em боломжийг дахин тооцоолно.',
    howToUseStandingsTitle: 'Байрлалын тайлбар унших',
    howToUseStandingsBody: 'Buchholz, seed болон дарааллын тэмдэглэл нь Swiss шатанд хослол, байрлал яагаад тийм байгааг тайлбарлана.',
    close: 'Хаах',
  },
  be: {
    // eSIM Ad
    supportUs: 'Падтрымайце нас - ',
    esimTitle: 'Атрымайце eSIM з эстонскім нумарам, без штомесячнай платы',
    esimDescription: 'Працуе ў 144 краінах • Ад €4 • Ад аўтара majors.im',
    esimDescription2: 'За €33 вы можаце атрымаць 50 ГБ даных, працуе ў ЕС, Вялікабрытаніі, ЗША, Кітаі, Малайзіі і Тайландзе',
    esimCta: 'Атрымаць eSIM',
    esimFeatures: 'Без кантрактаў • Без штомесячнай платы • Таныя тарыфы',
    esimSanctions: '',
    
    // Footer
    footerCopyright: '© 2019-2026',
    footerBusinessContact: 'Для рэкламы/бізнес-партнёрства, звяжыцеся',
    
    // Event Selector
    selectEvent: 'Выбраць падзею',
    languageAuto: 'Аўта',
    
    // About Menu
    about: 'Пра нас',
    majorRulebook: 'Правілы Major',
    majorRulebookSubtitleCS: 'Правілы Valve',
    majorRulebookSubtitleR6: 'Правілы R6 Major',
    discord: 'Discord',
    discordSubtitle: 'Водгукі або проста чат',
    kofi: 'Старонка Ko-fi',
    kofiSubtitle: 'Ахвяраваць на сайт :D',
    tutorial: 'Дапаможнік',
    tutorialSubtitle: 'Кароткі дапаможнік па выкарыстанні сайта',
    steamProfile: 'Профіль Steam',
    steamProfileSubtitle: 'Пакінуць каментар або ўзнагароду профілю',
    
    // UI Types
    classicUI: 'Класічны выгляд',
    bracketUI: 'Выгляд сеткі',
    minimalUI: 'Мінімальны выгляд',
    
    // Customize
    customize: 'Налады',
    displayMatchesOnly: 'Толькі матчы',
    hideEliminatedTeams: 'Схаваць выбыўшыя каманды',
    showBestOfIndicator: 'Паказаць фармат',
    showTeamStandings: 'Паказаць табліцу',
    teamAbbreviations: 'Скарачэнні каманд',
    themeMode: 'Тэма',
    systemTheme: 'Сістэмная',
    lightTheme: 'Светлая',
    darkTheme: 'Цёмная',
    
    // Actions
    advanceToNextStage: 'Перайсці да наступнага этапу',
    chooseUIType: 'Выгляд',
    statusQualified: 'кваліфікавалася',
    statusQuarterfinalist: 'чвэрцьфіналіст',
    statusSemifinalist: 'паўфіналіст',
    statusRunnerUp: 'фіналіст',
    statusChampion: 'чэмпіён',
    statusEliminated: 'выбыла',
    statusAdvanced: 'прайшла',
    finalResults: 'Выніковыя рэзультаты',
    finalResult: 'Выніковы рэзультат',
    roundLabel: 'Раунд',
    
    // How to Use
    howToUse: 'Як выкарыстоўваць',
    howToUseIntro: 'Гэты сімулятар дапамагае прагназаваць: выбірайце пераможцаў, глядзіце пераразлік сеткі і параўноўвайце ўплыў розных шляхоў.',
    howToUseStep1: 'Націсніце на каманду, якая, на вашу думку, выйграе матч',
    howToUseStep2: 'Пасля выбару ўсіх пераможцаў раунда наступны раунд абновіцца з атрыманымі парамі',
    howToUseStep3: 'Калі этап завершаны, перавядзіце каманды, што прайшлі, у наступны этап',
    howToUsePickTitle: 'Выбірайце пераможцаў',
    howToUseSimTitle: 'Пералічвайце сетку',
    howToUseAdvanceTitle: 'Прасоўвайце этапы',
    howToUseViewsTitle: 'Пераключайце выгляды',
    howToUseViewsBody: 'Мінімальны выгляд для хуткіх выбараў, сетка для поўнага шляху, класічны для шчыльнай табліцы і дэталяў.',
    howToUseLocksTitle: 'Фіксуйце важныя выбары',
    howToUseLocksBody: 'Паўторны клік па выбраным пераможцы фіксуе матч, таму shuffle або пазнейшыя змены не перазапішуць вынік.',
    pickemLockScenarioHelp: 'Націсніце выбранага пераможцу яшчэ раз, каб зафіксаваць вынік матча і пералічыць выжыванне Pick’em для гэтага сцэнарыя.',
    howToUseStandingsTitle: 'Чытайце кантэкст табліцы',
    howToUseStandingsBody: 'Buchholz, seed і парадак тлумачаць пары і месцы ў швейцарскіх этапах.',
    close: 'Зачыніць',
  },
};

// Supplementary translation keys, merged into the main table above.
// Kept in one place per language so new UI strings are easy to add and review.
const extraTranslations = {
  en: {
    lockBlockedToast: 'Finish or lock the earlier rounds before locking this match.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Advance',
    badgeEliminated: 'Eliminated',
    badgeAdvanceShort: 'Adv',
    badgeEliminatedShort: 'Elim',
    badgeLocked: 'Locked',
    badgeWrong: 'Wrong',
    shuffleMatchups: 'Shuffle matchups',
    matchLink: 'Match',
    resultsLink: 'Results',
  },
  zh: {
    lockBlockedToast: '请先完成或锁定前面的轮次，才能锁定此对局。',
    versus: 'vs',
    live: '直播',
    badgeAdvance: '晋级',
    badgeEliminated: '淘汰',
    badgeAdvanceShort: '晋级',
    badgeEliminatedShort: '淘汰',
    badgeLocked: '已锁定',
    badgeWrong: '错误',
    shuffleMatchups: '随机对阵',
    matchLink: '比赛',
    resultsLink: '结果',
  },
  es: {
    lockBlockedToast: 'Termina o bloquea las rondas anteriores antes de bloquear este partido.',
    versus: 'vs',
    live: 'En vivo',
    badgeAdvance: 'Avanza',
    badgeEliminated: 'Eliminado',
    badgeAdvanceShort: 'Av',
    badgeEliminatedShort: 'Elim',
    badgeLocked: 'Bloqueado',
    badgeWrong: 'Incorrecto',
    shuffleMatchups: 'Mezclar enfrentamientos',
    matchLink: 'Partido',
    resultsLink: 'Resultados',
  },
  ja: {
    lockBlockedToast: 'この試合をロックする前に、前のラウンドを終了またはロックしてください。',
    versus: 'vs',
    live: 'ライブ',
    badgeAdvance: '進出',
    badgeEliminated: '敗退',
    badgeAdvanceShort: '進出',
    badgeEliminatedShort: '敗退',
    badgeLocked: 'ロック',
    badgeWrong: '不正解',
    shuffleMatchups: '対戦をシャッフル',
    matchLink: '試合',
    resultsLink: '結果',
  },
  pt: {
    lockBlockedToast: 'Conclua ou bloqueie as rodadas anteriores antes de bloquear esta partida.',
    versus: 'vs',
    live: 'Ao vivo',
    badgeAdvance: 'Avança',
    badgeEliminated: 'Eliminado',
    badgeAdvanceShort: 'Av',
    badgeEliminatedShort: 'Elim',
    badgeLocked: 'Bloqueado',
    badgeWrong: 'Errado',
    shuffleMatchups: 'Embaralhar confrontos',
    matchLink: 'Partida',
    resultsLink: 'Resultados',
  },
  ru: {
    lockBlockedToast: 'Завершите или зафиксируйте предыдущие раунды, прежде чем фиксировать этот матч.',
    versus: 'vs',
    live: 'Лайв',
    badgeAdvance: 'Проход',
    badgeEliminated: 'Выбыл',
    badgeAdvanceShort: 'Прох',
    badgeEliminatedShort: 'Выб',
    badgeLocked: 'Зафикс.',
    badgeWrong: 'Неверно',
    shuffleMatchups: 'Перемешать пары',
    matchLink: 'Матч',
    resultsLink: 'Результаты',
  },
  pl: {
    lockBlockedToast: 'Zakończ lub zablokuj wcześniejsze rundy, zanim zablokujesz ten mecz.',
    versus: 'vs',
    live: 'Na żywo',
    badgeAdvance: 'Awans',
    badgeEliminated: 'Odpada',
    badgeAdvanceShort: 'Awans',
    badgeEliminatedShort: 'Odp',
    badgeLocked: 'Zablok.',
    badgeWrong: 'Błąd',
    shuffleMatchups: 'Losuj pary',
    matchLink: 'Mecz',
    resultsLink: 'Wyniki',
  },
  uk: {
    lockBlockedToast: 'Завершіть або зафіксуйте попередні раунди, перш ніж фіксувати цей матч.',
    versus: 'vs',
    live: 'Наживо',
    badgeAdvance: 'Прохід',
    badgeEliminated: 'Вибув',
    badgeAdvanceShort: 'Прох',
    badgeEliminatedShort: 'Виб',
    badgeLocked: 'Зафікс.',
    badgeWrong: 'Невірно',
    shuffleMatchups: 'Перемішати пари',
    matchLink: 'Матч',
    resultsLink: 'Результати',
  },
  de: {
    lockBlockedToast: 'Beende oder sperre die früheren Runden, bevor du dieses Match sperrst.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Weiter',
    badgeEliminated: 'Raus',
    badgeAdvanceShort: 'Weiter',
    badgeEliminatedShort: 'Raus',
    badgeLocked: 'Gesperrt',
    badgeWrong: 'Falsch',
    shuffleMatchups: 'Paarungen mischen',
    matchLink: 'Match',
    resultsLink: 'Ergebnisse',
  },
  fr: {
    lockBlockedToast: 'Terminez ou verrouillez les tours précédents avant de verrouiller ce match.',
    versus: 'vs',
    live: 'En direct',
    badgeAdvance: 'Qualifié',
    badgeEliminated: 'Éliminé',
    badgeAdvanceShort: 'Qual',
    badgeEliminatedShort: 'Élim',
    badgeLocked: 'Verrouillé',
    badgeWrong: 'Erroné',
    shuffleMatchups: 'Mélanger les matchs',
    matchLink: 'Match',
    resultsLink: 'Résultats',
  },
  tr: {
    lockBlockedToast: 'Bu maçı kilitlemeden önce önceki turları tamamlayın veya kilitleyin.',
    versus: 'vs',
    live: 'Canlı',
    badgeAdvance: 'Geçti',
    badgeEliminated: 'Elendi',
    badgeAdvanceShort: 'Geçti',
    badgeEliminatedShort: 'Elendi',
    badgeLocked: 'Kilitli',
    badgeWrong: 'Yanlış',
    shuffleMatchups: 'Eşleşmeleri karıştır',
    matchLink: 'Maç',
    resultsLink: 'Sonuçlar',
  },
  sv: {
    lockBlockedToast: 'Avsluta eller lås de tidigare omgångarna innan du låser den här matchen.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Vidare',
    badgeEliminated: 'Utslagen',
    badgeAdvanceShort: 'Vidare',
    badgeEliminatedShort: 'Ut',
    badgeLocked: 'Låst',
    badgeWrong: 'Fel',
    shuffleMatchups: 'Blanda matcher',
    matchLink: 'Match',
    resultsLink: 'Resultat',
  },
  nl: {
    lockBlockedToast: 'Voltooi of vergrendel de eerdere rondes voordat je deze wedstrijd vergrendelt.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Door',
    badgeEliminated: 'Uitgesch.',
    badgeAdvanceShort: 'Door',
    badgeEliminatedShort: 'Uit',
    badgeLocked: 'Vergrendeld',
    badgeWrong: 'Fout',
    shuffleMatchups: 'Wedstrijden husselen',
    matchLink: 'Wedstrijd',
    resultsLink: 'Resultaten',
  },
  cs: {
    lockBlockedToast: 'Před uzamčením tohoto zápasu dokončete nebo uzamkněte předchozí kola.',
    versus: 'vs',
    live: 'Živě',
    badgeAdvance: 'Postup',
    badgeEliminated: 'Vyřazen',
    badgeAdvanceShort: 'Postup',
    badgeEliminatedShort: 'Vyř',
    badgeLocked: 'Zamčeno',
    badgeWrong: 'Špatně',
    shuffleMatchups: 'Zamíchat zápasy',
    matchLink: 'Zápas',
    resultsLink: 'Výsledky',
  },
  fi: {
    lockBlockedToast: 'Päätä tai lukitse aiemmat kierrokset ennen tämän ottelun lukitsemista.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Jatkoon',
    badgeEliminated: 'Pudonnut',
    badgeAdvanceShort: 'Jatko',
    badgeEliminatedShort: 'Pois',
    badgeLocked: 'Lukittu',
    badgeWrong: 'Väärin',
    shuffleMatchups: 'Sekoita ottelut',
    matchLink: 'Ottelu',
    resultsLink: 'Tulokset',
  },
  ro: {
    lockBlockedToast: 'Finalizează sau blochează rundele anterioare înainte de a bloca acest meci.',
    versus: 'vs',
    live: 'Live',
    badgeAdvance: 'Avansează',
    badgeEliminated: 'Eliminat',
    badgeAdvanceShort: 'Av',
    badgeEliminatedShort: 'Elim',
    badgeLocked: 'Blocat',
    badgeWrong: 'Greșit',
    shuffleMatchups: 'Amestecă meciurile',
    matchLink: 'Meci',
    resultsLink: 'Rezultate',
  },
  mn: {
    lockBlockedToast: 'Энэ тоглолтыг түгжихээс өмнө өмнөх шатуудыг дуусгах эсвэл түгжинэ үү.',
    versus: 'vs',
    live: 'Шууд',
    badgeAdvance: 'Шалгарсан',
    badgeEliminated: 'Хасагдсан',
    badgeAdvanceShort: 'Шалг',
    badgeEliminatedShort: 'Хас',
    badgeLocked: 'Түгжсэн',
    badgeWrong: 'Буруу',
    shuffleMatchups: 'Тоглолтыг холих',
    matchLink: 'Тоглолт',
    resultsLink: 'Үр дүн',
  },
  be: {
    lockBlockedToast: 'Завяршыце або зафіксуйце папярэднія раунды, перш чым фіксаваць гэты матч.',
    versus: 'vs',
    live: 'Жыва',
    badgeAdvance: 'Праход',
    badgeEliminated: 'Выбыў',
    badgeAdvanceShort: 'Прах',
    badgeEliminatedShort: 'Выб',
    badgeLocked: 'Зафікс.',
    badgeWrong: 'Няверна',
    shuffleMatchups: 'Перамяшаць пары',
    matchLink: 'Матч',
    resultsLink: 'Вынікі',
  },
};

for (const lang of Object.keys(extraTranslations)) {
  translations[lang] = { ...(translations[lang] || {}), ...extraTranslations[lang] };
}

// Pick'em tracker + toolbar strings. "Pick'em" is kept as a brand term.
const extraTranslations2 = {
  en: {
    advanceStageShort: 'Advance',
    clear: 'Clear',
    save: 'Save',
    pickemDuplicateError: 'Each team can only be used once.',
    pickemImageError: 'Unable to save the Pick’em image.',
    pickemCalcError: 'Unable to calculate the Pick’em probability.',
    simulationTime: 'Simulation time',
    calculationProgress: 'Calculation progress',
    clearPickemTitle: 'Clear Pick’em picks and cached calculations',
    savePickemImageTitle: 'Save Pick’em image',
    sharePickemXTitle: 'Generate screenshot and share Pick’em on X',
    closeTeamPicker: 'Close team picker',
  },
  zh: {
    advanceStageShort: '晋级',
    clear: '清除',
    save: '保存',
    pickemDuplicateError: '每支队伍只能选择一次。',
    pickemImageError: '无法保存 Pick’em 图片。',
    pickemCalcError: '无法计算 Pick’em 概率。',
    simulationTime: '模拟时间',
    calculationProgress: '计算进度',
    clearPickemTitle: '清除 Pick’em 选择和缓存的计算',
    savePickemImageTitle: '保存 Pick’em 图片',
    sharePickemXTitle: '生成截图并在 X 上分享 Pick’em',
    closeTeamPicker: '关闭队伍选择器',
  },
  es: {
    advanceStageShort: 'Avanzar',
    clear: 'Borrar',
    save: 'Guardar',
    pickemDuplicateError: 'Cada equipo solo puede usarse una vez.',
    pickemImageError: 'No se pudo guardar la imagen de Pick’em.',
    pickemCalcError: 'No se pudo calcular la probabilidad de Pick’em.',
    simulationTime: 'Tiempo de simulación',
    calculationProgress: 'Progreso del cálculo',
    clearPickemTitle: 'Borrar selecciones de Pick’em y cálculos en caché',
    savePickemImageTitle: 'Guardar imagen de Pick’em',
    sharePickemXTitle: 'Generar captura y compartir Pick’em en X',
    closeTeamPicker: 'Cerrar selector de equipos',
  },
  ja: {
    advanceStageShort: '進出',
    clear: 'クリア',
    save: '保存',
    pickemDuplicateError: '各チームは一度しか選択できません。',
    pickemImageError: 'Pick’em 画像を保存できませんでした。',
    pickemCalcError: 'Pick’em 確率を計算できませんでした。',
    simulationTime: 'シミュレーション時間',
    calculationProgress: '計算の進行状況',
    clearPickemTitle: 'Pick’em の選択とキャッシュした計算をクリア',
    savePickemImageTitle: 'Pick’em 画像を保存',
    sharePickemXTitle: 'スクリーンショットを作成して X で Pick’em を共有',
    closeTeamPicker: 'チーム選択を閉じる',
  },
  pt: {
    advanceStageShort: 'Avançar',
    clear: 'Limpar',
    save: 'Salvar',
    pickemDuplicateError: 'Cada equipe só pode ser usada uma vez.',
    pickemImageError: 'Não foi possível salvar a imagem do Pick’em.',
    pickemCalcError: 'Não foi possível calcular a probabilidade do Pick’em.',
    simulationTime: 'Tempo de simulação',
    calculationProgress: 'Progresso do cálculo',
    clearPickemTitle: 'Limpar escolhas do Pick’em e cálculos em cache',
    savePickemImageTitle: 'Salvar imagem do Pick’em',
    sharePickemXTitle: 'Gerar captura e compartilhar Pick’em no X',
    closeTeamPicker: 'Fechar seletor de equipes',
  },
  ru: {
    advanceStageShort: 'Дальше',
    clear: 'Очистить',
    save: 'Сохранить',
    pickemDuplicateError: 'Каждую команду можно выбрать только один раз.',
    pickemImageError: 'Не удалось сохранить изображение Pick’em.',
    pickemCalcError: 'Не удалось рассчитать вероятность Pick’em.',
    simulationTime: 'Время симуляции',
    calculationProgress: 'Ход расчёта',
    clearPickemTitle: 'Очистить выбор Pick’em и кэшированные расчёты',
    savePickemImageTitle: 'Сохранить изображение Pick’em',
    sharePickemXTitle: 'Создать скриншот и поделиться Pick’em в X',
    closeTeamPicker: 'Закрыть выбор команды',
  },
  pl: {
    advanceStageShort: 'Awans',
    clear: 'Wyczyść',
    save: 'Zapisz',
    pickemDuplicateError: 'Każda drużyna może być użyta tylko raz.',
    pickemImageError: 'Nie udało się zapisać obrazu Pick’em.',
    pickemCalcError: 'Nie udało się obliczyć prawdopodobieństwa Pick’em.',
    simulationTime: 'Czas symulacji',
    calculationProgress: 'Postęp obliczeń',
    clearPickemTitle: 'Wyczyść wybory Pick’em i zapisane obliczenia',
    savePickemImageTitle: 'Zapisz obraz Pick’em',
    sharePickemXTitle: 'Zrób zrzut ekranu i udostępnij Pick’em na X',
    closeTeamPicker: 'Zamknij wybór drużyny',
  },
  uk: {
    advanceStageShort: 'Далі',
    clear: 'Очистити',
    save: 'Зберегти',
    pickemDuplicateError: 'Кожну команду можна вибрати лише один раз.',
    pickemImageError: 'Не вдалося зберегти зображення Pick’em.',
    pickemCalcError: 'Не вдалося обчислити ймовірність Pick’em.',
    simulationTime: 'Час симуляції',
    calculationProgress: 'Хід обчислення',
    clearPickemTitle: 'Очистити вибір Pick’em і кешовані обчислення',
    savePickemImageTitle: 'Зберегти зображення Pick’em',
    sharePickemXTitle: 'Створити знімок і поділитися Pick’em у X',
    closeTeamPicker: 'Закрити вибір команди',
  },
  de: {
    advanceStageShort: 'Weiter',
    clear: 'Leeren',
    save: 'Speichern',
    pickemDuplicateError: 'Jedes Team kann nur einmal verwendet werden.',
    pickemImageError: 'Pick’em-Bild konnte nicht gespeichert werden.',
    pickemCalcError: 'Pick’em-Wahrscheinlichkeit konnte nicht berechnet werden.',
    simulationTime: 'Simulationszeit',
    calculationProgress: 'Berechnungsfortschritt',
    clearPickemTitle: 'Pick’em-Auswahl und zwischengespeicherte Berechnungen löschen',
    savePickemImageTitle: 'Pick’em-Bild speichern',
    sharePickemXTitle: 'Screenshot erstellen und Pick’em auf X teilen',
    closeTeamPicker: 'Teamauswahl schließen',
  },
  fr: {
    advanceStageShort: 'Avancer',
    clear: 'Effacer',
    save: 'Enregistrer',
    pickemDuplicateError: 'Chaque équipe ne peut être utilisée qu’une seule fois.',
    pickemImageError: 'Impossible d’enregistrer l’image Pick’em.',
    pickemCalcError: 'Impossible de calculer la probabilité Pick’em.',
    simulationTime: 'Temps de simulation',
    calculationProgress: 'Progression du calcul',
    clearPickemTitle: 'Effacer les choix Pick’em et les calculs en cache',
    savePickemImageTitle: 'Enregistrer l’image Pick’em',
    sharePickemXTitle: 'Générer une capture et partager Pick’em sur X',
    closeTeamPicker: 'Fermer le sélecteur d’équipes',
  },
  tr: {
    advanceStageShort: 'İlerle',
    clear: 'Temizle',
    save: 'Kaydet',
    pickemDuplicateError: 'Her takım yalnızca bir kez kullanılabilir.',
    pickemImageError: 'Pick’em görüntüsü kaydedilemedi.',
    pickemCalcError: 'Pick’em olasılığı hesaplanamadı.',
    simulationTime: 'Simülasyon süresi',
    calculationProgress: 'Hesaplama ilerlemesi',
    clearPickemTitle: 'Pick’em seçimlerini ve önbelleğe alınan hesaplamaları temizle',
    savePickemImageTitle: 'Pick’em görüntüsünü kaydet',
    sharePickemXTitle: 'Ekran görüntüsü oluştur ve Pick’em’i X’te paylaş',
    closeTeamPicker: 'Takım seçiciyi kapat',
  },
  sv: {
    advanceStageShort: 'Vidare',
    clear: 'Rensa',
    save: 'Spara',
    pickemDuplicateError: 'Varje lag kan endast användas en gång.',
    pickemImageError: 'Det gick inte att spara Pick’em-bilden.',
    pickemCalcError: 'Det gick inte att beräkna Pick’em-sannolikheten.',
    simulationTime: 'Simuleringstid',
    calculationProgress: 'Beräkningsförlopp',
    clearPickemTitle: 'Rensa Pick’em-val och cachade beräkningar',
    savePickemImageTitle: 'Spara Pick’em-bild',
    sharePickemXTitle: 'Skapa skärmbild och dela Pick’em på X',
    closeTeamPicker: 'Stäng lagväljaren',
  },
  nl: {
    advanceStageShort: 'Verder',
    clear: 'Wissen',
    save: 'Opslaan',
    pickemDuplicateError: 'Elk team kan maar één keer worden gebruikt.',
    pickemImageError: 'Kan de Pick’em-afbeelding niet opslaan.',
    pickemCalcError: 'Kan de Pick’em-kans niet berekenen.',
    simulationTime: 'Simulatietijd',
    calculationProgress: 'Berekeningsvoortgang',
    clearPickemTitle: 'Pick’em-keuzes en opgeslagen berekeningen wissen',
    savePickemImageTitle: 'Pick’em-afbeelding opslaan',
    sharePickemXTitle: 'Schermafbeelding maken en Pick’em delen op X',
    closeTeamPicker: 'Teamkiezer sluiten',
  },
  cs: {
    advanceStageShort: 'Dál',
    clear: 'Vymazat',
    save: 'Uložit',
    pickemDuplicateError: 'Každý tým lze použít pouze jednou.',
    pickemImageError: 'Obrázek Pick’em se nepodařilo uložit.',
    pickemCalcError: 'Pravděpodobnost Pick’em se nepodařilo vypočítat.',
    simulationTime: 'Čas simulace',
    calculationProgress: 'Průběh výpočtu',
    clearPickemTitle: 'Vymazat tipy Pick’em a uložené výpočty',
    savePickemImageTitle: 'Uložit obrázek Pick’em',
    sharePickemXTitle: 'Vytvořit snímek a sdílet Pick’em na X',
    closeTeamPicker: 'Zavřít výběr týmu',
  },
  fi: {
    advanceStageShort: 'Jatka',
    clear: 'Tyhjennä',
    save: 'Tallenna',
    pickemDuplicateError: 'Jokaista joukkuetta voi käyttää vain kerran.',
    pickemImageError: 'Pick’em-kuvan tallennus epäonnistui.',
    pickemCalcError: 'Pick’em-todennäköisyyden laskenta epäonnistui.',
    simulationTime: 'Simulaatioaika',
    calculationProgress: 'Laskennan edistyminen',
    clearPickemTitle: 'Tyhjennä Pick’em-valinnat ja välimuistiin tallennetut laskelmat',
    savePickemImageTitle: 'Tallenna Pick’em-kuva',
    sharePickemXTitle: 'Luo kuvakaappaus ja jaa Pick’em X:ssä',
    closeTeamPicker: 'Sulje joukkuevalitsin',
  },
  ro: {
    advanceStageShort: 'Avansează',
    clear: 'Șterge',
    save: 'Salvează',
    pickemDuplicateError: 'Fiecare echipă poate fi folosită o singură dată.',
    pickemImageError: 'Imaginea Pick’em nu a putut fi salvată.',
    pickemCalcError: 'Probabilitatea Pick’em nu a putut fi calculată.',
    simulationTime: 'Timp de simulare',
    calculationProgress: 'Progresul calculului',
    clearPickemTitle: 'Șterge alegerile Pick’em și calculele din cache',
    savePickemImageTitle: 'Salvează imaginea Pick’em',
    sharePickemXTitle: 'Generează captură și distribuie Pick’em pe X',
    closeTeamPicker: 'Închide selectorul de echipe',
  },
  mn: {
    advanceStageShort: 'Дараах',
    clear: 'Цэвэрлэх',
    save: 'Хадгалах',
    pickemDuplicateError: 'Баг тус бүрийг зөвхөн нэг удаа сонгож болно.',
    pickemImageError: 'Pick’em зургийг хадгалж чадсангүй.',
    pickemCalcError: 'Pick’em магадлалыг тооцоолж чадсангүй.',
    simulationTime: 'Симуляцийн хугацаа',
    calculationProgress: 'Тооцооллын явц',
    clearPickemTitle: 'Pick’em сонголт болон хадгалсан тооцооллыг цэвэрлэх',
    savePickemImageTitle: 'Pick’em зургийг хадгалах',
    sharePickemXTitle: 'Дэлгэцийн зураг үүсгэж Pick’em-г X дээр хуваалцах',
    closeTeamPicker: 'Багийн сонгогчийг хаах',
  },
  be: {
    advanceStageShort: 'Далей',
    clear: 'Ачысціць',
    save: 'Захаваць',
    pickemDuplicateError: 'Кожную каманду можна выбраць толькі адзін раз.',
    pickemImageError: 'Не ўдалося захаваць выяву Pick’em.',
    pickemCalcError: 'Не ўдалося разлічыць імавернасць Pick’em.',
    simulationTime: 'Час сімуляцыі',
    calculationProgress: 'Ход разліку',
    clearPickemTitle: 'Ачысціць выбар Pick’em і кэшаваныя разлікі',
    savePickemImageTitle: 'Захаваць выяву Pick’em',
    sharePickemXTitle: 'Стварыць здымак і падзяліцца Pick’em у X',
    closeTeamPicker: 'Закрыць выбар каманды',
  },
};

for (const lang of Object.keys(extraTranslations2)) {
  translations[lang] = { ...(translations[lang] || {}), ...extraTranslations2[lang] };
}

const SUPPORTED_LANGUAGES = ['en', 'zh', 'es', 'ja', 'pt', 'ru', 'pl', 'uk', 'de', 'fr', 'tr', 'sv', 'nl', 'cs', 'fi', 'ro', 'mn', 'be'];
const LANGUAGE_STORAGE_KEY = 'majorsim.language.v2';
const LEGACY_LANGUAGE_STORAGE_KEY = 'language';

const removeLegacyLanguagePreference = () => {
  try {
    localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY);
  } catch (e) {
    // Ignore storage access errors.
  }
};

/**
 * Detect language from browser
 * @returns {string} Language code (en, zh, es, ja, pt)
 */
const detectBrowserLanguage = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }
  
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const lang = browserLang.toLowerCase();
  
  if (lang.startsWith('zh')) return 'zh';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('ja')) return 'ja';
  if (lang.startsWith('pt')) return 'pt';
  if (lang.startsWith('ru')) return 'ru';
  if (lang.startsWith('pl')) return 'pl';
  if (lang.startsWith('uk')) return 'uk';
  if (lang.startsWith('de')) return 'de';
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('tr')) return 'tr';
  if (lang.startsWith('sv')) return 'sv';
  if (lang.startsWith('nl')) return 'nl';
  if (lang.startsWith('cs')) return 'cs';
  if (lang.startsWith('fi')) return 'fi';
  if (lang.startsWith('ro')) return 'ro';
  if (lang.startsWith('mn')) return 'mn';
  if (lang.startsWith('be')) return 'be';
  
  return 'en';
};

/**
 * Get current language from localStorage or browser
 * @returns {string} Language code
 */
export const getCurrentLanguage = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  removeLegacyLanguagePreference();

  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored === 'auto' || !stored) {
    return detectBrowserLanguage();
  }
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }
  
  // Fallback to browser detection
  return detectBrowserLanguage();
};

export const isChineseLanguage = (lang = getCurrentLanguage()) => (
  typeof lang === 'string' && lang.toLowerCase().startsWith('zh')
);

/**
 * Set language preference
 * @param {string} lang - Language code
 */
export const setLanguage = (lang) => {
  if (typeof window !== 'undefined' && SUPPORTED_LANGUAGES.includes(lang)) {
    removeLegacyLanguagePreference();
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: lang }));
  }
};

/**
 * Get translation for a key
 * @param {string} key - Translation key
 * @param {string} lang - Optional language code, defaults to current language
 * @returns {string} Translated text
 */
export const t = (key, lang = null) => {
  const currentLang = lang || getCurrentLanguage();
  const translation = translations[currentLang];

  if (!translation) {
    return translations.en[key] || key;
  }

  return translation[key] || translations.en[key] || key;
};

/**
 * React hook for translations
 */
export const useTranslation = () => {
  const [lang, setLangState] = React.useState(getCurrentLanguage());

  React.useEffect(() => {
    const handleLanguageChange = (e) => {
      setLangState(e.detail);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  return {
    t: (key) => t(key, lang),
    language: lang,
    setLanguage: (newLang) => {
      setLanguage(newLang);
      setLangState(getCurrentLanguage());
    },
  };
};
